import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
    ArrowLeft, Check, Download, Edit,Search, X, 
    Filter, MoreVertical, MessageCircle, Eye, Calendar as CalIcon, CreditCard, User as UserIcon,
    Wallet, Trash2, Banknote, RefreshCw, TrendingUp, AlertTriangle, Info, Clock,
    Volume2, VolumeX, HelpCircle, PlayCircle, Layers, MinusCircle, Coins, CheckCircle, Menu, Settings
} from 'lucide-react';

import jsPDF from 'jspdf'; 
import autoTable from 'jspdf-autotable'; 
import { useSmartGuide } from './useSmartGuide';

import Card from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import Tabs from '../../ui/Tabs';
import ConfirmModal from '../../ui/ConfirmModal';
import SmartGuideModal from '../../ui/SmartGuideModal';
import PdfExportModal from '../../ui/PdfExportModal';
import Modal from '../../ui/Modal';

const API_BASE = 'https://mukadampro.shirpur.store';



// --- HELPER FUNCTIONS ---
const getToday = () => new Date().toISOString().split("T")[0];
const getFirstDayOfMonth = () => {
    const date = new Date();
    date.setDate(1);
    return date.toISOString().split('T')[0];
};

const formatCurrency = (amount) => {
  return "Rs. " + (Number(amount) || 0).toLocaleString('en-IN');
};

const formatDate = (dateStr) => {
    if(!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString('en-IN', {
        day: 'numeric', month: 'short', year: 'numeric'
    });
};

const formatDateTime = (dateStr) => {
    if(!dateStr) return "-";
    const d = new Date(dateStr);
    const hasTime = dateStr.includes(' ') || dateStr.includes(':');
    if (hasTime) {
        return d.toLocaleString('en-IN', {
            day: 'numeric', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: true
        });
    }
    return formatDate(dateStr);
};

const formatLoomRanges = (loomStr) => {
    if (!loomStr) return "-";
    const nums = [...new Set(loomStr.toString().split(',')
        .map(s => parseInt(s.replace(/[^0-9]/g, ''), 10))
        .filter(n => !isNaN(n))
        .sort((a, b) => a - b))];
    if (nums.length === 0) return "-";
    let ranges = [];
    let start = nums[0];
    const fmt = n => `L${String(n).padStart(2, '0')}`;
    for (let i = 1; i <= nums.length; i++) {
        if (i === nums.length || nums[i] !== nums[i-1] + 1) {
            let end = nums[i-1];
            if (start === end) ranges.push(fmt(start));
            else if (end === start + 1) ranges.push(`${fmt(start)}, ${fmt(end)}`);
            else ranges.push(`${fmt(start)} to ${fmt(end)}`);
            start = nums[i];
        }
    }
    return ranges.join(', ');
};

// --- VOICE FORMATTERS ---
const formatVoiceCurrency = (amt) => {
    const num = Number(amt) || 0;
    const rupees = Math.floor(num);
    const paise = Math.round((num - rupees) * 100);
    if (paise > 0) return `${rupees} रुपये ${paise} पैसे`;
    return `${rupees} रुपये`; 
};

const formatVoiceNumber = (num) => {
    const n = Number(num) || 0;
    if (Number.isInteger(n) || n % 1 === 0) return Math.floor(n).toString(); 
    const parts = n.toString().split('.');
    return `${parts[0]} पॉइंट ${parts[1]}`;
};

// --- ANIMATION ---
const SuccessAnimation = ({ type, onComplete }) => {
    useEffect(() => {
        const timer = setTimeout(() => { onComplete(); }, 1500); 
        return () => clearTimeout(timer);
    }, [onComplete]);

    const color = type === 'online' ? '#10B981' : (type === 'delete' ? '#D32F2F' : '#3b82f6'); 
    const label = type === 'online' ? 'Online Record Saved' : (type === 'delete' ? 'Transaction Deleted' : 'Cash Record Saved');
    const icon = type === 'delete' ? <Trash2 size={40} color="white" strokeWidth={3} /> : <Check size={40} color="white" strokeWidth={3} />;

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
            background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(10px)', zIndex: 10005,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            animation: 'fadeIn 0.3s ease-out'
        }}>
            <div style={{
                width:'80px', height:'80px', borderRadius:'50%', background:color, 
                display:'flex', alignItems:'center', justifyContent:'center', 
                boxShadow:`0 10px 30px -10px ${color}`, 
                animation:'scaleUp 0.5s forwards'
            }}>
                {icon}
            </div>
            <h2 style={{marginTop:'24px', fontSize:'16px', fontWeight:'600', color:'#111', animation:'fadeIn 0.5s 0.3s forwards', opacity:0}}>
                {label}
            </h2>
        </div>
    );
};

// --- MAIN COMPONENT ---
const PaymentView = ({ onBack, workers, refreshData, appBg, globalGuide }) => {
    const [activeTab, setActiveTab] = useState('new');
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);

    // --- USE CUSTOM HOOK FOR VOICE ---
    const {
        voiceAssist, showGuideModal, setShowGuideModal,
        activeGuideFlow, currentGuideMessage,
        toggleVoiceAssist,
        handleFocusVoice, handleSelectVoice, handleTextBlurVoice,
        startSmartGuide, endSmartGuide, advanceNextStep,
        speakHinglish, setPendingConfirm
    } = globalGuide;
    
    // डबल-क्लिक की मेमोरी
    const clickMemory = useRef({ id: null, time: 0 });

   // --- SMART DOUBLE-CLICK VOICE WRAPPER ---
    const handleVoiceClick = (e, voiceText, actionFunction, actionId = null, isAlreadyOpen = false, alreadyOpenMsg = null) => {
        if (e) { e.preventDefault(); e.stopPropagation(); }

        if (isAlreadyOpen) {
            if (voiceAssist && !activeGuideFlow && alreadyOpenMsg) {
                speakHinglish(alreadyOpenMsg);
            }
            return;
        }

        if (!voiceAssist || activeGuideFlow) {
            if (actionFunction) actionFunction();
            return;
        }

        if (!actionFunction) {
            speakHinglish(voiceText);
            return;
        }

        const uid = actionId || voiceText;
        const now = Date.now();
        const timeDiff = now - clickMemory.current.time;

        if (clickMemory.current.id === uid && timeDiff < 20000) {
            if (actionFunction) actionFunction();
            clickMemory.current = { id: null, time: 0 }; 
        } else {
            let confirmMsg = voiceText;

            if (!confirmMsg.includes('दोबारा क्लिक')) {
                if (actionId === 'tab-new') {
                    confirmMsg += " क्या आप किसी वर्कर को सैलरी या एडवांस पैसे देना चाहते हैं? तो इस बटन पर दोबारा क्लिक करें।";
                } else if (actionId === 'tab-history') {
                    confirmMsg += " क्या आप पुरानी ट्रांज़ैक्शन हिस्ट्री और रिपोर्ट्स देखना चाहते हैं? तो इस बटन पर दोबारा क्लिक करें।";
                } else if (actionId === 'btn-pay-sal') {
                    confirmMsg += " क्या आप वर्कर को सैलरी देना चाहते हैं? तो इस बटन पर दोबारा क्लिक करें।";
                } else if (actionId === 'btn-give-adv') {
                    confirmMsg += " क्या आप वर्कर को एडवांस Paise देना चाहते हैं? तो इस बटन पर दोबारा क्लिक करें।";
                } else if (actionId === 'btn-save-payment') {
                    confirmMsg += " क्या आप इस Salary पेमेंट ट्रांज़ैक्शन को सेव करना चाहते हैं? तो इस बटन पर दोबारा क्लिक करें।";
                } else if (actionId === 'btn-download-report') {
                    confirmMsg += " क्या आप Salary रिपोर्ट डाउनलोड करना चाहते हैं? तो इस बटन पर दोबारा क्लिक करें।";
                } else if (actionId && String(actionId).startsWith('hist-')) {
                    confirmMsg += " क्या आप इस ट्रांज़ैक्शन की पूरी डिटेल देखना चाहते हैं? तो इस पर दोबारा क्लिक करें।";
                } else if (actionId === 'btn-edit-record') {
                    confirmMsg += " क्या आप इस Salary रिकॉर्ड को एडिट करना चाहते हैं? तो इस बटन पर दोबारा क्लिक करें।";
                } else if (actionId === 'btn-share-receipt') {
                    confirmMsg += " क्या आप इस पेमेंट Receipt शेयर करना चाहते हैं? तो इस बटन पर दोबारा क्लिक करें।";
                } else if (actionId === 'btn-update-record') {
                    confirmMsg += " क्या आप इस Salary रिकॉर्ड को अपडेट करना चाहते हैं? तो इस बटन पर दोबारा क्लिक करें।";
                } else if (actionId === 'btn-save-pdf') {
                    confirmMsg += " क्या आप पीडीएफ रिपोर्ट Download करना चाहते हैं? तो इस बटन पर दोबारा क्लिक करें।";
                } else if (actionId === 'btn-back') {
                    confirmMsg += " क्या आप पीछे जाना चाहते हैं? तो इस बटन पर दोबारा क्लिक करें।";
                } else if (actionId === 'filter-salary') {
    confirmMsg += " सिर्फ Total Salary Paid का रिकॉर्ड देखने के लिए दोबारा क्लिक करें।";
} else if (actionId === 'filter-advance') {
    confirmMsg += " सिर्फ एडवांस का रिकॉर्ड देखने के लिए दोबारा क्लिक करें।";
} else if (actionId === 'filter-recovered') {
    confirmMsg += " सिर्फ Recovered का रिकॉर्ड देखने के लिए दोबारा क्लिक करें।";
} else if (actionId && String(actionId).startsWith('app-')) {                } else if (actionId && String(actionId).startsWith('app-')) {
                    confirmMsg += " क्या आप यह पेमेंट ऐप चुनना चाहते हैं? तो इस पर दोबारा क्लिक करें।";
                } else if (actionId === 'btn-confirm-action') {
                    confirmMsg += " क्या आप कन्फर्म करना चाहते हैं? तो इस बटन पर दोबारा क्लिक करें।";
                } else if (actionId === 'btn-cancel-action') {
                    confirmMsg += " क्या आप कैंसल करना चाहते हैं? तो इस बटन पर दोबारा क्लिक करें।";
                } else {
                    confirmMsg += " क्या आप इसमें जाना चाहते हैं? तो इस पर दोबारा क्लिक करें।";
                }
            }

            speakHinglish(confirmMsg);
            clickMemory.current = { id: uid, time: now }; 
        }
    };

    // UI State
    const [menuOpenId, setMenuOpenId] = useState(null);
    const [showReportModal, setShowReportModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false); 
    const [showViewModal, setShowViewModal] = useState(false); 
    const [successAnim, setSuccessAnim] = useState({ show: false, type: 'cash' });
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef(null);
    // Custom Alert/Confirm State
    const [alertConfig, setAlertConfig] = useState({ show: false, title: '', msg: '', type: 'info' }); 
    const [confirmConfig, setConfirmConfig] = useState({ show: false, msg: '', onConfirm: null });

    // Data
    const [ownerProfile, setOwnerProfile] = useState(null);
    const [reportConfig, setReportConfig] = useState({ workerId: '', startDate: getFirstDayOfMonth(), endDate: getToday() });

    // Form State
    const [selectedWorkerId, setSelectedWorkerId] = useState('');
    const [paymentType, setPaymentType] = useState('salary'); 
    const [amount, setAmount] = useState('');
    const [baseMode, setBaseMode] = useState('cash'); 
    const [onlineApp, setOnlineApp] = useState('PhonePe');
    const [date, setDate] = useState(getToday());
    const [remarks, setRemarks] = useState('');
    const [salaryMode, setSalaryMode] = useState('weekly'); 

    // Stats / Calc
    const [prodMeters, setProdMeters] = useState(0);
    const [rate, setRate] = useState(0);
    const [advanceDed, setAdvanceDed] = useState(0); 
    const [currentBalance, setCurrentBalance] = useState(0);
    const [isFetchingStats, setIsFetchingStats] = useState(false);
    const [prodStartDate, setProdStartDate] = useState(null);
    const [prodEndDate, setProdEndDate] = useState(null);
    const [prodDays, setProdDays] = useState(0);

    // Filters
    const [historyFilterId, setHistoryFilterId] = useState('');
    const [statFilterType, setStatFilterType] = useState('all'); 
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);

    // Edit/View Data
    const [editData, setEditData] = useState(null);
    const [viewData, setViewData] = useState(null); 

   // Click Outside Hook (Fixed: Inside click filters added to protect dropdown unmounting)
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isMenuOpen && menuRef.current && !menuRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
            
            // Agar actions dropdown khula hai toh tabhi close karein jab click uske bahar ho
            if (menuOpenId !== null) {
                const activeContainer = document.getElementById(`actions-wrapper-${menuOpenId}`);
                if (activeContainer && !activeContainer.contains(event.target)) {
                    setMenuOpenId(null);
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isMenuOpen, menuOpenId]);

    // --- VOICE CLOSING WRAPPER ---
    const closeModalsVoice = (e) => {
        if(e) { e.preventDefault(); e.stopPropagation(); }
        
        if (window.speechSynthesis) {
            window.speechSynthesis.cancel(); 
        }

        setPendingConfirm(null); 
        clickMemory.current = { id: null, time: 0 }; 
        
        if (activeGuideFlow) {
            endSmartGuide(true);
        }

        setShowReportModal(false);
        setShowEditModal(false);
        setShowViewModal(false);
        setShowGuideModal(false);
    };

    // --- CUSTOM ALERT HELPERS ---
    const showAlert = (msg, type = 'info', title = 'Notice') => {
        if(voiceAssist && !activeGuideFlow) speakHinglish(msg);
        setAlertConfig({ show: true, title, msg, type });
    };
    const closeAlert = () => setAlertConfig({ ...alertConfig, show: false });

    const showConfirm = (msg, onConfirm) => {
        if(voiceAssist && !activeGuideFlow) speakHinglish(msg);
        setConfirmConfig({ show: true, msg, onConfirm });
    };
    const closeConfirm = () => setConfirmConfig({ ...confirmConfig, show: false });
    const handleConfirmAction = () => {
        if(confirmConfig.onConfirm) confirmConfig.onConfirm();
        closeConfirm();
    };

    // --- FETCH DATA ---
    const fetchData = async () => {
        setIsLoadingHistory(true);
        try {
            const [resHist, resProf] = await Promise.all([
                fetch(`${API_BASE}/payment_api.php?action=get_history`),
                fetch(`${API_BASE}/get_owner_profile.php`)
            ]);
            const jsonHist = await resHist.json();
            if(jsonHist.status === 'success') setPaymentHistory(jsonHist.data);
            const jsonProf = await resProf.json();
            if(jsonProf.status === 'success') setOwnerProfile(jsonProf.data);
        } catch(e) { console.error("History Error", e); } 
        finally { setIsLoadingHistory(false); }
    };

    useEffect(() => { fetchData(); }, []);

    // --- WORKER STATS ---
    const fetchWorkerStats = async (wId, mode, pType = paymentType, workerObj = null, speakStats = false) => {
        if(!wId) return;
        setIsFetchingStats(true);
        try {
            const res = await fetch(`${API_BASE}/get_worker_stats.php?id=${wId}&mode=${mode}`);
            const json = await res.json();
            if(json.status === 'success') {
                const metersVal = parseFloat(json.meters) || 0;
                setProdMeters(metersVal);

                const sDate = json.start_date || getToday();
                const eDate = json.end_date || getToday();
                setProdStartDate(sDate);
                setProdEndDate(eDate);
                
                const calculatedDays = json.total_days || Math.max(1, Math.ceil(Math.abs(new Date(eDate) - new Date(sDate)) / (1000 * 60 * 60 * 24)));
                setProdDays(calculatedDays);
                
                if (speakStats && voiceAssist && !activeGuideFlow) {
                    const w = workerObj || workers.find(x => String(x.id) === String(wId));
                    const wName = w ? w.name : "Worker";
                    
                    if (pType === 'salary') {
                        const r = parseFloat(w?.ratePerMeter || w?.rate_per_meter || 0);
                        const expectedSalary = (metersVal * r).toFixed(0);
                        
                        setTimeout(() => {
                            speakHinglish(`${wName} ने ${formatDate(sDate)} से ${formatDate(eDate)} तक, टोटल ${calculatedDays} दिनों में ${formatVoiceNumber(metersVal)} Meter Production किया है, जिसका टोटल ${formatVoiceCurrency(expectedSalary)} बनता है।`);
                        }, 800);
                        
                    } else {
                        const adv = parseFloat(w?.advance_balance || 0);
                        setTimeout(() => {
                            speakHinglish(`${wName} का पुराना Advance ${formatVoiceCurrency(adv)} बाकी है।`);
                        }, 500);
                    }
                }
            }
        } catch(e) { console.error("Stats Error", e); } 
        finally { setIsFetchingStats(false); }
    };

    const handleWorkerChange = (e) => {
        const newId = e.target.value;
        setSelectedWorkerId(newId);
        setAmount(''); setRemarks(''); 
        if(newId) {
            const w = workers.find(x => String(x.id) === String(newId));
            if(w) {
                setRate(parseFloat(w.ratePerMeter || w.rate_per_meter || 0));
                setCurrentBalance(parseFloat(w.advance_balance || 0));
                setAdvanceDed(0); 
                fetchWorkerStats(newId, salaryMode, paymentType, w, true); 
            }
        } else { setRate(0); setProdMeters(0); setCurrentBalance(0); setAdvanceDed(0); }
    };

    useEffect(() => {
        if(selectedWorkerId) fetchWorkerStats(selectedWorkerId, salaryMode, paymentType, null, true);
    }, [salaryMode]);

    useEffect(() => {
        if(paymentType === 'salary') {
            const gross = (parseFloat(prodMeters)||0) * (parseFloat(rate)||0);
            const net = gross - (parseFloat(advanceDed)||0);
            setAmount(net > 0 ? net.toFixed(2) : '');
        }
    }, [prodMeters, rate, advanceDed, paymentType]);

   // --- LOGIC: Duplicate Salary Check & Save (UPI INTEGRATION ADDED) ---
    const handlePaymentSave = async (e) => {
        if (e) e.preventDefault();
        
        if(!selectedWorkerId || !amount || parseFloat(amount) <= 0) {
            showAlert("ध्यान दें, आपने सही रकम नहीं भरी है।", "error", "Invalid Amount");
            return;
        }

        if (paymentType === 'salary') {
            const isAlreadyPaid = paymentHistory.some(p => 
                String(p.worker_id) === String(selectedWorkerId) && 
                p.type === 'salary' && 
                p.salary_period === salaryMode
            );

            if (isAlreadyPaid) {
                showAlert(" आप इस वर्कर को पहले ही Salary दे चुके हैं।", "error", "Duplicate Payment");
                return; 
            }
        }

        // 👇 100% FREE UPI DEEP LINK LOGIC 👇
        if (baseMode === 'online') {
            const worker = workers.find(w => String(w.id) === String(selectedWorkerId));
            // डेटाबेस में UPI ID जिस नाम से सेव है, वो यहाँ चेक करें
            const upiId = worker?.upi_id || worker?.upi || worker?.upi_id_number || worker?.upi_number;

            if (upiId) {
                // Dynamic UPI Link (Specific app targeting)
                let upiPrefix = 'upi://pay';
                if (onlineApp === 'PhonePe') upiPrefix = 'phonepe://pay';
                else if (onlineApp === 'Paytm') upiPrefix = 'paytmmp://pay';
                else if (onlineApp === 'GPay') upiPrefix = 'tez://upi/pay';
                
                // फाइनल लिंक
                const upiLink = `${upiPrefix}?pa=${upiId}&pn=${encodeURIComponent(worker.name)}&am=${amount}&cu=INR`;
                
                if (voiceAssist && !activeGuideFlow) {
                    speakHinglish("पेमेंट ऐप खोला जा रहा है। अपना UPI PIN डालकर पेमेंट पूरी करें।");
                }
                
                // सबसे पक्का तरीका (Anchor Tag Method)
                setTimeout(() => {
                    const a = document.createElement('a');
                    a.href = upiLink;
                    // fallback के लिए अगर स्पेसिफिक ऐप न खुले तो नार्मल upi:// चलाये
                    a.onclick = () => {
                        setTimeout(() => {
                           window.location.href = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(worker.name)}&am=${amount}&cu=INR`;
                        }, 500);
                    };
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                }, 800); // 800ms का इंतज़ार ताकि वॉइस मैसेज प्ले हो सके

            } else {
                showAlert("इस वर्कर की UPI ID प्रोफाइल में सेव नहीं है! कृपया पहले वर्कर की प्रोफाइल एडिट करके UPI ID ऐड करें।", "error", "UPI ID Missing");
                return; // Stop transaction because UPI is missing
            }
        }
        // 👆 100% FREE UPI DEEP LINK LOGIC 👆

        let finalMode = baseMode === 'online' ? `online - ${onlineApp.toLowerCase()}` : 'cash';
        
        const now = new Date();
        const h = String(now.getHours()).padStart(2, '0');
        const m = String(now.getMinutes()).padStart(2, '0');
        const s = String(now.getSeconds()).padStart(2, '0');
        const finalDateTime = `${date} ${h}:${m}:${s}`;
        
        const payload = {
            transaction_id: null,
            worker_id: selectedWorkerId,
            amount: amount, 
            type: paymentType, 
            mode: finalMode,
            date: finalDateTime,
            remarks: remarks,
            advance_recovered: paymentType === 'salary' ? advanceDed : 0,
            salary_period: paymentType === 'salary' ? salaryMode : null,
            
            prod_start_date: prodStartDate || getToday(),
            prod_end_date: prodEndDate || getToday(),
            total_production: paymentType === 'salary' ? prodMeters : 0,
            rate: paymentType === 'salary' ? rate : 0
        };

        saveTransaction(payload);
    };

    const handleEditSave = () => {
        if(!editData.amount || parseFloat(editData.amount) <= 0) {
            showAlert(" ध्यान दें, आपने सही रकम नहीं भरी है।", "error", "Invalid Amount");
            return;
        }
        let finalMode = editData.baseMode === 'online' ? `online - ${editData.onlineApp.toLowerCase()}` : 'cash';
        const isSalary = editData.type === 'salary';
        
        const payload = {
            transaction_id: editData.id,
            worker_id: editData.worker_id,
            amount: editData.amount,
            type: editData.type,
            mode: finalMode,
            date: editData.date,
            remarks: editData.remarks,
            advance_recovered: isSalary ? editData.advanceDed : 0,
            salary_period: isSalary ? editData.salaryMode : null,
            is_edited: 1,
            
            prod_start_date: editData.prod_start_date || null,
            prod_end_date: editData.prod_end_date || null,
            total_production: isSalary ? (editData.total_production || 0) : 0,
            rate: isSalary ? (editData.rate || 0) : 0
        };
        saveTransaction(payload);
        closeModalsVoice();
    };

    const handleDeleteClick = (txnId) => {
        setMenuOpenId(null);
        showConfirm(
            " क्या आप वाकई इस ट्रांज़ैक्शन को Delete करना चाहते हैं? इससे वर्कर का बैलेंस वापस पहले जैसा हो जाएगा।",
            () => deleteTransaction(txnId)
        );
    };

    const deleteTransaction = async (txnId) => {
        try {
            const res = await fetch(`${API_BASE}/payment_api.php?action=delete_transaction&id=${txnId}`);
            const json = await res.json();
            if(json.status === 'success') {
                if(voiceAssist && !activeGuideFlow) speakHinglish(" रिकॉर्ड हमेशा के लिए Delete हो गया है।");
                setSuccessAnim({ show: true, type: 'delete' });
            } else {
                showAlert("Delete Failed: " + json.message, "error");
            }
        } catch(e) {
            showAlert("Network Error: Could not delete.", "error");
        }
    };

    const saveTransaction = async (payload) => {
        try {
            const res = await fetch(`${API_BASE}/payment_api.php`, { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify(payload) });
            const json = await res.json();
            if(json.status === 'success') {
                if(voiceAssist && !activeGuideFlow) speakHinglish(payload.mode.includes('online') ? " Online Salary रिकॉर्ड सेव हो गया है।" : " कैश Salary रिकॉर्ड सेव हो गया है।");
                setSuccessAnim({ show: true, type: payload.mode.includes('online') ? 'online' : 'cash' });
            } else { 
                showAlert("Error: " + json.message, "error"); 
            }
        } catch(e) { showAlert("Network Error", "error"); }
    };

    const onAnimationComplete = useCallback(() => {
        setSuccessAnim({ show: false, type: 'cash' });
        setAmount(''); setRemarks(''); setProdMeters(0); setAdvanceDed(0);
        fetchData(); refreshData(); 
    }, [refreshData]);

    const handleEditClick = (txn) => {
        const modeStr = (txn.mode || '').toLowerCase();
        let bMode = 'cash';
        let app = 'PhonePe';
        if (modeStr.includes('online')) {
            bMode = 'online';
            const parts = modeStr.split('-');
            app = parts.length > 1 ? parts[1].trim() : 'PhonePe';
        }

        setEditData({
            id: txn.id,
            worker_id: txn.worker_id,
            worker_name: txn.worker_name,
            type: txn.type, 
            amount: txn.amount,
            date: txn.date.split(' ')[0],
            originalTime: txn.date.includes(' ') ? txn.date.split(' ')[1] : null,
            remarks: txn.remarks || '',
            advanceDed: parseFloat(txn.advance_adjusted || 0),
            baseMode: bMode,
            onlineApp: app,
            salaryMode: txn.salary_period || 'weekly'
        });
        
        if (voiceAssist && !activeGuideFlow) speakHinglish(" आपने एडिट ट्रांज़ैक्शन Button ओपन किया है।");
        setShowEditModal(true);
    };

    const handleViewClick = (txn) => {
        setViewData(txn);
        setShowViewModal(true);
    };

    const handleMenuClick = (e, id) => { e.stopPropagation(); setMenuOpenId(menuOpenId === id ? null : id); };

    const handleWhatsAppShare = (txn) => {
        if (voiceAssist && !activeGuideFlow) speakHinglish(" Receipt व्हाट्सप्प पर शेयर की जा रही है।");
        const worker = workers.find(w => String(w.id) === String(txn.worker_id));
        const workerName = worker ? worker.name : " Worker";
        const currentBal = worker ? worker.advance_balance : "0";
        const msg = `*Payment Receipt*\nTo: ${workerName}\n\nAmount: ₹${txn.amount}\nType: ${txn.type.toUpperCase()}\nDate: ${formatDate(txn.date)}\nBalance: ₹${currentBal}`;
        window.open(`https://wa.me/91${worker?.mobile || ''}?text=${encodeURIComponent(msg)}`, '_blank');
    };

   const filteredHistory = useMemo(() => {
        let data = paymentHistory;
        if(historyFilterId) data = data.filter(p => String(p.worker_id) === String(historyFilterId));
        if(statFilterType === 'salary') data = data.filter(p => p.type === 'salary');
        else if (statFilterType === 'advance') data = data.filter(p => p.type === 'advance');
        else if (statFilterType === 'recovered') data = data.filter(p => p.type === 'salary' && parseFloat(p.advance_adjusted) > 0);
        
        // Date filter logic added here
        if (startDate) data = data.filter(p => p.date.split(' ')[0] >= startDate);
        if (endDate) data = data.filter(p => p.date.split(' ')[0] <= endDate);

        return data;
    }, [paymentHistory, historyFilterId, statFilterType, startDate, endDate]);

    const stats = useMemo(() => {
        let data = { sTotal: 0, aTotal: 0, rTotal: 0 };
        let baseData = paymentHistory;
        if(historyFilterId) baseData = baseData.filter(p => String(p.worker_id) === String(historyFilterId));
        baseData.forEach(p => {
            const amt = parseFloat(p.amount) || 0;
            const rec = parseFloat(p.advance_adjusted) || 0;
            if(p.type === 'salary') { data.sTotal += amt; data.rTotal += rec; } 
            else if (p.type === 'advance') { data.aTotal += amt; }
        });
        return data;
    }, [paymentHistory, historyFilterId]);

   // --- 🌟 PREMIUM LANDSCAPE PDF GENERATION (Matched with PdfExportModal) 🌟 ---
    const handleDownloadPdf = () => {
        let data = paymentHistory;
        if(reportConfig.workerId) data = data.filter(p => String(p.worker_id) === String(reportConfig.workerId));
        if(reportConfig.startDate) data = data.filter(p => p.date.split(' ')[0] >= reportConfig.startDate);
        if(reportConfig.endDate) data = data.filter(p => p.date.split(' ')[0] <= reportConfig.endDate);

        const owner = ownerProfile || {};
        const worker = reportConfig.workerId ? workers.find(w => String(w.id) === String(reportConfig.workerId)) : null;

        const TEXTILE_INFO = {
            name: owner.textile_name ? owner.textile_name.toUpperCase() : "MUKADAMPRO TEXTILES",
            owner: owner.owner_name ? owner.owner_name.toUpperCase() : "OWNER NAME",
            mobile: owner.mobile || "-",
            email: owner.email || "-",
            gst: owner.gst_number || owner.gstin || "-",
            address: owner.address || "-",
            totalLooms: owner.total_looms || owner.total_factory_looms || "-"
        };

        // Landscape format for matching layout
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const centerX = 297 / 2;

        const drawWatermark = () => {
            doc.setTextColor(240, 245, 250); 
            doc.setFontSize(45);
            doc.setFont("helvetica", "bold");
            doc.text(TEXTILE_INFO.name, centerX, 110, { align: "center", angle: 35 });
        };

        drawWatermark();

        const originalAddPage = doc.addPage.bind(doc);
        doc.addPage = function(...args) {
            originalAddPage(...args);
            drawWatermark(); 
            return this;     
        };

        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.setTextColor(15, 23, 42); 
        doc.text("SALARY/ADVANCE PAYMENT REPORT", centerX, 15, { align: "center" });

        const downloadTime = new Date().toLocaleString('en-IN', { 
            day: 'numeric', month: 'short', year: 'numeric', 
            hour: '2-digit', minute: '2-digit', hour12: true 
        });
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        doc.text(`Downloaded: ${downloadTime}`, 297 - 14, 15, { align: "right" });

        doc.setFontSize(20);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(51, 65, 85);
        doc.text(TEXTILE_INFO.name, centerX, 24, { align: "center" });

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(100, 116, 139);

        const line1 = `Owner: ${TEXTILE_INFO.owner}   |   Total Looms: ${TEXTILE_INFO.totalLooms}   |   Mobile: ${TEXTILE_INFO.mobile}`;
        doc.text(line1, centerX, 30, { align: "center" });

        const line2 = `Email: ${TEXTILE_INFO.email}   |   GST No: ${TEXTILE_INFO.gst}`;
        doc.text(line2, centerX, 35, { align: "center" });

        const line3 = `Address: ${TEXTILE_INFO.address}`;
        doc.text(line3, centerX, 40, { align: "center" });

        doc.setDrawColor(226, 232, 240); 
        doc.setLineWidth(0.5);
        doc.line(14, 44, 297 - 14, 44);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(15, 23, 42);
        
        let summaryTitle = "Grand Summary (All Workers)";
        if(worker) {
            summaryTitle = `Summary: ${worker.name.toUpperCase()}`;
        }
        
        const fromDateStr = reportConfig.startDate ? new Date(reportConfig.startDate).toLocaleDateString("en-GB") : '-';
        const toDateStr = reportConfig.endDate ? new Date(reportConfig.endDate).toLocaleDateString("en-GB") : '-';
        const dateRangeText = `Period: ${fromDateStr} TO ${toDateStr}`;
        
        doc.text(summaryTitle, centerX, 50, { align: "center" }); 
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(dateRangeText, centerX, 55, { align: "center" });

        const tableBody = [];
        let totalEarnedSum = 0;
        let totalRecoverySum = 0;
        let totalPaidSum = 0;

        data.forEach(p => {
            const isSalary = p.type === 'salary';
            const amount = parseFloat(p.amount) || 0;
            const recovery = parseFloat(p.advance_adjusted) || 0;
            const totalEarned = isSalary ? (amount + recovery) : 0;
            
            if(isSalary) {
                totalEarnedSum += totalEarned;
                totalRecoverySum += recovery;
            }
            totalPaidSum += amount;

            tableBody.push([
                formatDate(p.date), 
                p.worker_name.toUpperCase(),      
                p.type.toUpperCase(), 
                isSalary ? (p.salary_period ? p.salary_period.toUpperCase() : '-') : '-', 
                isSalary ? `Rs. ${totalEarned.toFixed(0)}` : '-', 
                isSalary && recovery > 0 ? `Rs. ${recovery.toFixed(0)}` : '-', 
                `Rs. ${amount.toFixed(0)}`, 
                (p.mode || '').toUpperCase()
            ]);
        });

        // 🌟 GRAND TOTAL ROW 🌟
        tableBody.push([
            { content: 'GRAND TOTAL', colSpan: 4, styles: { fontStyle: 'bold', halign: 'right', fillColor: [241, 245, 249], textColor: [15, 23, 42] } },
            { content: `Rs. ${totalEarnedSum.toFixed(0)}`, styles: { fontStyle: 'bold', fillColor: [241, 245, 249], textColor: [15, 23, 42] } },
            { content: `Rs. ${totalRecoverySum.toFixed(0)}`, styles: { fontStyle: 'bold', fillColor: [241, 245, 249], textColor: [15, 23, 42] } },
            { content: `Rs. ${totalPaidSum.toFixed(0)}`, styles: { fontStyle: 'bold', fillColor: [241, 245, 249], textColor: [15, 23, 42] } },
            { content: '', styles: { fillColor: [241, 245, 249] } }
        ]);

        autoTable(doc, {
            head: [['Date', 'Worker Name', 'Type', 'Period', 'Total Earned', 'Less Recov.', 'Net Paid', 'Mode']],
            body: tableBody,
            startY: 60, 
            theme: 'grid',
            headStyles: { fillColor: [15, 23, 42], textColor: 255, fontSize: 9, halign: 'center' },
            alternateRowStyles: { fillColor: [247, 247, 247] },
            styles: { fillColor: [255, 255, 255], fontSize: 8.5, halign: 'center', cellPadding: 4, textColor: [15, 23, 42] },
            columnStyles: { 0: { fontStyle: 'bold', halign: 'left' }, 1: { halign: 'left', fontStyle: 'bold' } },
            margin: { left: 14, right: 14 }
        });

        const totalPages = doc.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.setTextColor(100, 116, 139);
            doc.text(`Page ${i} of ${totalPages}`, 297 - 14, 210 - 10, { align: "right" });
        }
        
        doc.save(`Payment_Report_${reportConfig.startDate || getToday()}.pdf`); 
        if(voiceAssist && !activeGuideFlow) speakHinglish(" आपकी पीडीएफ रिपोर्ट फाइल सफलतापूर्वक डाउनलोड हो गई है।");
        closeModalsVoice();
    };

    // --- DYNAMIC VOICE MESSAGE GENERATOR FOR HISTORY ITEMS ---
    const getTransactionVoiceMsg = (p) => {
        const workerName = p.worker_name;
        const dateObj = new Date(p.date);
        const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
        const dateFormatted = `${dateObj.getDate()} ${months[dateObj.getMonth()]} ${dateObj.getFullYear()}`;
        const modeClean = (p.mode || '').replace('-', ' '); 
        
        if (p.type === 'salary') {
            const periodStr = p.salary_period === 'monthly' ? 'महीने' : 'हफ्ते';
            const recovery = parseFloat(p.advance_adjusted) || 0;
            const paid = parseFloat(p.amount) || 0;
            const total = paid + recovery;

            if (recovery > 0) {
                return `${workerName} को ${dateFormatted} को ${periodStr} की सैलरी की रकम ${formatVoiceCurrency(total)} है, लेकिन सैलरी से एडवांस रकम ${formatVoiceCurrency(recovery)} काट लिए गए हैं, इसलिए ${formatVoiceCurrency(paid)} ${modeClean} दिए हैं।`;
            } else {
                return `${workerName} को ${dateFormatted} को ${periodStr} की सैलरी ${formatVoiceCurrency(paid)} ${modeClean} दिए गए हैं।`;
            }
        } else if (p.type === 'advance') {
            const paid = parseFloat(p.amount) || 0;
            return `${workerName} को ${dateFormatted} को एडवांस रकम ${formatVoiceCurrency(paid)} ${modeClean} दिए गए हैं।`;
        }
        return `यह ${workerName} का रिकॉर्ड है जिसकी रकम ${formatVoiceCurrency(p.amount)} है।`;
    };

    // 🌟 1. NAYA LOGIC: यह चेक करेगा कि कोई भी पॉपअप खुला है या नहीं
    const isAnyModalOpen = showGuideModal || showReportModal || showEditModal || showViewModal || alertConfig.show || confirmConfig.show;

    // ===== 🌟 PREMIUM SCROLL LOCK (Hostinger / Mobile Fix) 🌟 =====
    useEffect(() => {
        if (isAnyModalOpen) {
            document.body.style.overflow = 'hidden'; 
        } else {
            document.body.style.overflow = 'auto';   
        }
        return () => { document.body.style.overflow = 'auto'; };
    }, [isAnyModalOpen]);

    return (
        // 🌟 2. CONTAINER UPDATE: पॉपअप खुले होने पर बैकग्राउंड का स्क्रॉल (overflowY) लॉक कर देगा
        <div style={{
            ...styles.container, 
            overflowY: isAnyModalOpen ? 'hidden' : 'auto'
        }}>
            <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap'); 
    * { font-family: 'Plus Jakarta Sans', sans-serif; box-sizing: border-box; } 
    @keyframes zoomIn { 0% {transform: scale(0.9); opacity: 0;} 100% {transform: scale(1); opacity: 1;} }
    @keyframes scaleUp { 0% {transform:scale(0);opacity:0;} 60% {transform:scale(1.1);opacity:1;} 100% {transform:scale(1);opacity:1;} }
    @keyframes fadeIn { from {opacity:0;} to {opacity:1;} }
    @keyframes pulseGuide { 0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); } 70% { transform: scale(1); box-shadow: 0 0 0 8px rgba(16, 185, 129, 0); } 100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); } }
    
  /* Spotlight Magic CSS */
    .smart-guide-target {
  box-shadow: 0 0 0 4px #10B981, 0 0 20px #10B981, 0 0 0 9999px rgba(0, 0, 0, 0.75) !important;
  outline: 3px solid white !important;
  background-color: #fff !important; 
  color: #000 !important;
  transition: all 0.3s ease !important;
  position: relative !important;
  z-index: 9999999 !important;
  pointer-events: auto !important;
}

/* 🌟 SUPER OVERRIDE: Kisi bhi global setting ya setting-color ko block karne ke liye 🌟 */
.smart-guide-target, 
.smart-guide-target *, 
.smart-guide-target .force-white,
.smart-guide-target [style*="color"] {
    color: #000 !important;
    -webkit-text-fill-color: #000 !important; /* Mobile browsers ke liye */
}

/* 🌟 Icons ko bhi black karne ke liye 🌟 */
.smart-guide-target svg, 
.smart-guide-target svg *,
.smart-guide-target .force-white svg {
    stroke: #000 !important;
    color: #000 !important;
}

    /* 🌟 NAYA LOGIC: Smart Guide Highlight hone par Button ka text Black karne ke liye 🌟 */
.smart-guide-target, .smart-guide-target * {
    color: #000 !important;
}
.smart-guide-target svg {
    stroke: #000 !important;
    color: #000 !important;
}

    /* 👇 YAHAN SE NAYA CODE ADD KIYA HAIN (Settings ko override karne ke liye) 👇 */
    .force-blue { color: #3B82F6 !important; }
    .force-red { color: #D32F2F !important; }
    .force-green { color: #10B981 !important; }
    .force-white-btn-text { 
        color: #ffffff !important; 
        -webkit-text-fill-color: #ffffff !important; 
    }
   /* 🚀 APPLE-LIKE ULTRA SMOOTH EXPERIENCE (120Hz FEEL) 🚀 */
    * { 
        -webkit-tap-highlight-color: transparent; 
        scroll-behavior: smooth; 
        text-rendering: optimizeLegibility;
        font-size: 14px !important; /* 🌟 14px Text Size Lock kiya */
    }
    body { 
        -webkit-font-smoothing: antialiased; 
        -moz-osx-font-smoothing: grayscale; 
        overscroll-behavior-y: none; 
        zoom: 0.95 !important; /* 🌟 95% Layout Zoom Lock kiya */
    }
    /* Invisible Scrollbar (iOS jaisa feel dene ke liye) */
    ::-webkit-scrollbar { display: none; width: 0px; background: transparent; } 
    
    /* Smooth 60fps/120fps Button & Input Animations */
    button, input, select {
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
        will-change: transform, opacity;
    }

`}</style>
            
            {successAnim.show && <SuccessAnimation type={successAnim.type} onComplete={onAnimationComplete} />}
            <button id="trigger-pdf-download" style={{display: 'none'}} onClick={() => setShowReportModal(true)}></button>
       
       {/* ===== 🌟 EXACT VACANCIES MATCHED HEADER SYSTEM (PaymentView.js - SYNCED SIZE) 🌟 ===== */}
            <div style={{
                background: '#ffffff', borderBottom: '1px solid #e2e8f0', boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
                padding: '15px 16px', display: 'flex', alignItems: 'center', justifyContent:'space-between', 
                position:'sticky', top:0, zIndex: 1000, pointerEvents: activeGuideFlow ? 'none' : 'auto'
            }}>
                <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
                    <button onClick={(e) => handleVoiceClick(e, " यह बैक बटन है, यहाँ से आप पिछले पेज पर वापस जा सकते हैं।", onBack, 'btn-back')} style={{ background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', cursor: 'pointer', padding: '4px', display:'flex', alignItems:'center' }}>
                        <ArrowLeft size={18} color="#475569"/>
                    </button>
                    <h1 style={{fontSize:'16px', fontWeight:'700', color:'#0F172A', margin:0, letterSpacing:'-0.5px'}}>Finance & Pay</h1>
                </div>
                
               <div style={{display:'flex', gap:'8px', alignItems: 'center'}}>
                    
                    {/* SMART GUIDE HELP BUTTON */}
                    <button 
                        onClick={() => {
                            speakHinglish(" Smart Live Guide मेनू ओपन हो गया है, आप किस Topic के बारे में सीखना चाहते हैं? नीचे दिए गए options में से चुनें, 1) Worker को Salary Payment कैसे दें?, 2) Salary/Advance Payment रिकॉर्ड की (PDF) Report कैसे डाउनलोड करें?, 3) Salary/Advance Payment रिकॉर्ड की Receipt कैसे शेयर करें?, 4) Salary/Advance Payment रिकॉर्ड को Edit कैसे करें?, 5) Worker को Advance Payment कैसे दें? ");
                            setShowGuideModal(true);
                        }} 
                        style={{
                            padding: '6px 8px', borderRadius: '10px', height: '32px',
                            background: 'linear-gradient(135deg, #E0F2FE, #7DD3FC)', border: '1px solid #BAE6FD',
                            boxShadow: '0 2px 6px rgba(2, 132, 199, 0.15)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s', boxSizing: 'border-box'
                        }}
                    >
                        <HelpCircle size={16} color="#0284C7"/>
                    </button>

                    {/* 🌟 HAMBURGER MENU DROPDOWN (100% UNIFIED WITH HOMEPAGE DESIGN) 🌟 */}
                    <div ref={menuRef} style={{ position: 'relative' }}>
                        <button 
                            onClick={() => setIsMenuOpen(!isMenuOpen)} 
                            style={{ 
                                width: '32px', height: '32px', borderRadius: '10px', 
                                border: '1px solid #e2e8f0', background: isMenuOpen ? '#f1f5f9' : '#f8fafc', 
                                display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.02)', transition: 'all 0.2s'
                            }}
                        >
                            <Menu size={16} color="#0F172A" />
                        </button>

                        {isMenuOpen && (
                            <>
                                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 99 }} onClick={() => setIsMenuOpen(false)} />
                               <div style={{ 
                                    position: 'absolute', top: '120%', right: 0, background: '#ffffff', 
                                    border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', 
                                    padding: '6px', display: 'flex', flexDirection: 'column', gap: '4px', 
                                    minWidth: '220px', zIndex: 100 
                                }}>
                                    
                                    {/* 1. MY PROFILE & SETTINGS CONTROL */}
                                    <button onClick={(e) => {
                                        setIsMenuOpen(false);
                                        handleVoiceClick(e, " ये कंट्रोल केंद्र है, यहाँ से आप प्रोफाइल और सेटिंग्स बदल सकते हैं।", () => {
                                            const triggerHomeBtn = document.querySelector('button[id="nav-btn-profile"]') || document.querySelector('.nav-item-profile');
                                            if (triggerHomeBtn) triggerHomeBtn.click();
                                            if (onBack) onBack(); 
                                        }, 'btn-profile');
                                    }} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', borderRadius: '8px', background: 'transparent', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left' }}>
                                        <Settings size={14} color="#475569" />
                                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#475569' }}>Profile & Settings</span>
                                    </button>

                                    {/* 🌟 REVENUE DIVIDER SEPARATOR 🌟 */}
                                    <div style={{ height: '1px', background: 'rgba(0, 0, 0, 0.05)', margin: '2px 4px', borderRadius: '1px' }}></div>

                                    {/* 2. AUTOMATED PAYMENTS REPORT CONTROLLER */}
                                    <button onClick={(e) => {
                                        setIsMenuOpen(false);
                                        handleVoiceClick(e, " पेमेंट रिपोर्ट डाउनलोड करने के लिए इसे दबाएं।", () => setShowReportModal(true), 'menu-payment-report-trigger');
                                    }} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', borderRadius: '8px', background: 'transparent', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left' }}>
                                        <Download size={14} color="#0F172A" />
                                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#0F172A' }}>Payments Report</span>
                                    </button>

                                </div>
                            </>
                        )}
                    </div>

                </div>
            </div>
            
            <div style={{...styles.content, pointerEvents: activeGuideFlow ? 'none' : 'auto'}}>
               {/* 🌟 PREMIUM CUSTOM TABS FOR FINANCE (Pill Switch Design) 🌟 */}
                <div style={{ 
                    display: 'flex', 
                    background: '#F1F5F9', /* 🌟 अनसिलेक्टेड बॉक्स का डार्क/ग्रे बैकग्राउंड */
                    padding: '4px', 
                    borderRadius: '16px', /* पिल (Pill) शेप */
                    marginBottom: '16px',
                    boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.05)', /* अंदर की तरफ 3D शैडो */
                    border: '1px solid rgba(226, 232, 240, 0.8)'
                }}>
                    <button id="tab-new" onClick={(e)=>handleVoiceClick(e, " यह ट्रांज़ैक्शन Button है, यहाँ आप Workers को Salary और Advance दे सकते हैं।", ()=>setActiveTab('new'), 'tab-new', activeTab === 'new', " यह ट्रांज़ैक्शन Button है, यहाँ आप नई Salary और Advance Workers को दे सकते हैं।")} 
                    style={activeTab === 'new' 
                        ? { flex: 1, padding: '8px 12px', background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.04)', borderRadius: '12px', fontWeight: '700', color: '#0F172A', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', cursor: 'pointer', transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)', fontSize: '11px', textTransform: 'uppercase', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }
                        : { flex: 1, padding: '8px 12px', background: 'transparent', border: '1px solid transparent', borderRadius: '12px', fontWeight: '600', color: '#64748B', cursor: 'pointer', transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)', fontSize: '11px', textTransform: 'uppercase', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}
                    >
                        <div style={{ background: activeTab === 'new' ? 'linear-gradient(135deg, #D1FAE5, #6EE7B7)' : 'transparent', padding: '4px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: activeTab === 'new' ? '0 2px 6px rgba(16, 185, 129, 0.2)' : 'none' }}>
                            <CreditCard size={14} color={activeTab === 'new' ? '#047857' : '#94A3B8'}/>
                        </div>
                        TRANSACTION
                    </button>
                    
                    <button id="tab-history" onClick={(e)=>handleVoiceClick(e, " यह History और Reports Button है, यहाँ आप पिछली Salary और Advance रिकॉर्ड देख सकते हैं।", ()=>{setActiveTab('history'); fetchData();}, 'tab-history', activeTab === 'history', "यह हिस्ट्री और रिपोर्ट्स Button है, यहाँ आप पिछली Salary और Advance रिकॉर्ड देख सकते हैं।")} 
                    style={activeTab === 'history'
                        ? { flex: 1, padding: '8px 12px', background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.04)', borderRadius: '12px', fontWeight: '700', color: '#0F172A', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', cursor: 'pointer', transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)', fontSize: '11px', textTransform: 'uppercase', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }
                        : { flex: 1, padding: '8px 12px', background: 'transparent', border: '1px solid transparent', borderRadius: '12px', fontWeight: '600', color: '#64748B', cursor: 'pointer', transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)', fontSize: '11px', textTransform: 'uppercase', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}
                    >
                        <div style={{ background: activeTab === 'history' ? 'linear-gradient(135deg, #E0F2FE, #7DD3FC)' : 'transparent', padding: '4px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: activeTab === 'history' ? '0 2px 6px rgba(2, 132, 199, 0.2)' : 'none' }}>
                            <Clock size={14} color={activeTab === 'history' ? '#0369A1' : '#94A3B8'}/>
                        </div>
                        HISTORY & REPORTS
                    </button>
                </div>

               {activeTab === 'new' && (
    <div className="premium-glass animate-in" style={{ 
        background: 'rgba(255, 255, 255, 0.45)', 
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        border: '1px solid rgba(255, 255, 255, 0.6)',
        borderRadius: '28px',
        padding: '16px',
        boxShadow: '0 12px 35px rgba(0,0,0,0.03), inset 0 1px 0 rgba(255,255,255,1)',
        display: 'flex', 
        flexDirection: 'column', 
        gap: '16px',
        animationDelay: '0.1s'
    }}>
        
        {/* === SUB-CARD 1: WORKER PROFILE SELECTION BOARD === */}
        <div style={{
            background: 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            borderRadius: '20px',
            padding: '16px',
            boxShadow: '0 6px 20px rgba(15, 23, 42, 0.02), inset 0 1px 2px rgba(255,255,255,0.8)',
            border: '1px solid rgba(15, 23, 42, 0.04)',
            position: 'relative',
            overflow: 'hidden'
        }}>
            {/* Radial Gradient Glow Circle */}
            <div style={{ position: 'absolute', top: '-30px', right: '-30px', width: '90px', height: '90px', background: 'radial-gradient(circle, #6366F1 0%, transparent 70%)', opacity: 0.1, filter: 'blur(20px)', borderRadius: '50%', zIndex: 0 }} />
            
            <div style={{ position: 'relative', zIndex: 1 }}>
                <label style={styles.fieldLabel}>Select Worker Profile</label>
                <div style={{ position: 'relative' }}>
                    <div style={{ position: 'absolute', top: '50%', left: '12px', transform: 'translateY(-50%)', width: '28px', height: '28px', borderRadius: '50%', background: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.05)' }}>
                        {selectedWorkerId && workers.find(w => String(w.id) === String(selectedWorkerId))?.profile_image ? (
                            <img src={workers.find(w => String(w.id) === String(selectedWorkerId)).profile_image} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" />
                        ) : (
                            <UserIcon size={14} color="#64748B" />
                        )}
                    </div>
                    <select 
                        id="select-worker" 
                        style={{ ...styles.input, paddingLeft: '48px', height: '44px', borderRadius: '14px', appearance: 'none', WebkitAppearance: 'none', background: '#F8FAFC' }} 
                        value={selectedWorkerId} 
                        onFocus={() => handleFocusVoice(" यहाँ Salary या Advance देने के लिए वर्कर चुनें।")}
                        onChange={handleWorkerChange}
                    >
                        <option value="">-- Choose Worker --</option>
                        {workers.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                    </select>
                    <div style={{ position: 'absolute', top: '50%', right: '14px', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#64748B' }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                    </div>
                </div>
                {selectedWorkerId && (
                    <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'flex-end' }}>
                        <span className="force-red" style={{ fontSize: '9px', fontWeight: '800', background: '#FEF2F2', color: '#EF4444', padding: '5px 12px', borderRadius: '100px', border: '1px solid #FEE2E2', letterSpacing: '0.3px', boxShadow: '0 2px 6px rgba(239, 68, 68, 0.05)' }}>
                            Advance Due: ₹{currentBalance}
                        </span>
                    </div>
                )}
            </div>
        </div>

        {/* === SUB-CARD 2: PAYMENT TYPE & TIMELINE CONTROLLER PANEL === */}
        <div style={{
            background: 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            padding: '14px',
            boxShadow: '0 6px 20px rgba(15, 23, 42, 0.02), inset 0 1px 2px rgba(255,255,255,0.8)',
            border: '1px solid rgba(15, 23, 42, 0.04)'
        }}>
            <label style={{ ...styles.fieldLabel, marginBottom: '8px' }}>Transaction Mode Setup</label>
            <div style={{ 
                display: 'flex', 
                background: '#F1F5F9', 
                padding: '4px', 
                borderRadius: '14px', 
                boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.05)', 
                border: '1px solid rgba(226, 232, 240, 0.8)'
            }}>
                <button 
                    id="btn-pay-sal" 
                    onClick={(e)=>handleVoiceClick(e, " ye सैलरी बटन है, यहाँ से आप वर्कर को सैलरी दे सकते हैं,", ()=>setPaymentType('salary'), 'btn-pay-sal', paymentType === 'salary', " ye सैलरी बटन है, यहाँ से आप वर्कर को सैलरी दे सकते हैं।")} 
                    style={paymentType === 'salary' 
                        ? { flex: 1, padding: '10px 12px', background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.04)', borderRadius: '10px', fontWeight: '800', color: '#0F172A', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', cursor: 'pointer', fontSize: '11px', textTransform: 'uppercase', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px' } 
                        : { flex: 1, padding: '10px 12px', background: 'transparent', border: '1px solid transparent', borderRadius: '10px', fontWeight: '600', color: '#64748B', cursor: 'pointer', fontSize: '11px', textTransform: 'uppercase', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                >
                    <CheckCircle size={13} color={paymentType === 'salary' ? '#10B981' : '#64748B'} strokeWidth={paymentType === 'salary' ? 2.5 : 2} />
                    PAY SALARY
                </button>

                <button 
                    id="btn-give-adv" 
                    onClick={(e)=>handleVoiceClick(e, " ये एडवांस बटन है, यहाँ से आप वर्कर को एडवांस पैसे दे सकते हैं,", ()=>{setPaymentType('advance'); setAmount('');}, 'btn-give-adv', paymentType === 'advance', " ये एडवांस बटन है, यहाँ से आप वर्कर को एडवांस पैसे दे सकते हैं।")} 
                    style={paymentType === 'advance' 
                        ? { flex: 1, padding: '10px 12px', background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.04)', borderRadius: '10px', fontWeight: '800', color: '#0F172A', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', cursor: 'pointer', fontSize: '11px', textTransform: 'uppercase', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px' } 
                        : { flex: 1, padding: '10px 12px', background: 'transparent', border: '1px solid transparent', borderRadius: '10px', fontWeight: '600', color: '#64748B', cursor: 'pointer', fontSize: '11px', textTransform: 'uppercase', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                >
                    <Coins size={13} color={paymentType === 'advance' ? '#EF4444' : '#64748B'} strokeWidth={paymentType === 'advance' ? 2.5 : 2} />
                    GIVE ADVANCE
                </button>
            </div>
        </div>

        {/* === SUB-CARD 3: LIVE PRODUCTION DYNAMIC CALCULATOR GRAPHIC === */}
        {paymentType === 'salary' && selectedWorkerId && (
            <div style={{
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                borderRadius: '20px',
                padding: '16px',
                boxShadow: '0 6px 20px rgba(15, 23, 42, 0.02), inset 0 1px 0 rgba(255,255,255,1)',
                border: '1px solid rgba(15, 23, 42, 0.04)',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                gap: '14px'
            }}>
                <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '100px', height: '100px', background: 'radial-gradient(circle, #38BDF8 0%, transparent 70%)', opacity: 0.12, filter: 'blur(25px)', borderRadius: '50%', zIndex: 0 }} />

                <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #F1F5F9', paddingBottom: '10px' }}>
                        <div id="salary-period-box" style={{ display: 'flex', background: '#F1F5F9', padding: '3px', borderRadius: '20px' }}>
                            <button 
                                id="btn-salary-weekly" 
                                onClick={(e)=>handleVoiceClick(e, ` क्या आप ${workers.find(w => String(w.id) === String(selectedWorkerId))?.name || ' इस वर्कर'} को हफ्ते की Salary देना चाहते हैं, तो इस current वीक पर दोबारा क्लिक करें।`, ()=>setSalaryMode('weekly'), 'btn-sal-week', salaryMode === 'weekly', " हफ्ते की Salary पहले से चुनी है।")} 
                                style={{ padding: '6px 14px', borderRadius: '16px', border: 'none', fontSize: '10px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s', textTransform: 'uppercase', background: salaryMode === 'weekly' ? '#FFFFFF' : 'transparent', color: salaryMode === 'weekly' ? '#0F172A' : '#64748B', boxShadow: salaryMode === 'weekly' ? '0 2px 6px rgba(0,0,0,0.05)' : 'none' }}
                            >
                                Week
                            </button>
                            <button 
                                id="btn-salary-monthly" 
                                onClick={(e)=>handleVoiceClick(e, ` क्या आप ${workers.find(w => String(w.id) === String(selectedWorkerId))?.name || ' इस वर्कर'} को महीने की Salary देना चाहते हैं, तो इस current month पर दोबारा क्लिक करें।`, ()=>setSalaryMode('monthly'), 'btn-sal-month', salaryMode === 'monthly', " महीने की Salary पहले से चुनी है।")} 
                                style={{ padding: '6px 14px', borderRadius: '16px', border: 'none', fontSize: '10px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s', textTransform: 'uppercase', background: salaryMode === 'monthly' ? '#FFFFFF' : 'transparent', color: salaryMode === 'monthly' ? '#0F172A' : '#64748B', boxShadow: salaryMode === 'monthly' ? '0 2px 6px rgba(0,0,0,0.05)' : 'none' }}
                            >
                                Month
                            </button>
                        </div>

                        {prodStartDate && prodEndDate && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', background: '#EFF6FF', padding: '5px 10px', borderRadius: '100px', border: '1px solid #DBEAFE' }}>
                                <CalIcon size={11} color="#2563EB" />
                                <span style={{ fontSize: '9px', color: '#1E40AF', fontWeight: '700' }}>
                                    {prodDays} Days Logged
                                </span>
                            </div>
                        )}
                    </div>

                    {prodStartDate && prodEndDate && (
                        <div style={{ fontSize: '11px', color: '#475569', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px', background: '#F8FAFC', padding: '8px 12px', borderRadius: '12px', border: '1px solid #E2E8F0', marginTop: '-4px' }}>
                            <Clock size={12} color="#94A3B8" />
                            <span>Cycle: {new Date(prodStartDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} - {new Date(prodEndDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                        </div>
                    )}

                    <div style={styles.row}>
                        <div>
                            <label style={styles.fieldLabel}>{salaryMode === 'weekly' ? 'Weekly' : 'Monthly'} Meters</label>
                            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '38px', background: '#F8FAFC', borderRight: '1px solid #E2E8F0', borderRadius: '12px 0 0 12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Layers size={14} color="#64748B" />
                                </div>
                                <input 
                                    id="input-prod-meters"
                                    type="number" 
                                    style={{ ...styles.input, paddingLeft: '46px', height: '42px', borderRadius: '12px' }} 
                                    value={prodMeters} 
                                    onFocus={() => {
                                        const wName = workers.find(w => String(w.id) === String(selectedWorkerId))?.name || " इस वर्कर";
                                        handleFocusVoice(`${wName} ने ${formatDate(prodStartDate)} से ${formatDate(prodEndDate)} तक, टोटल ${prodDays} दिनों में ${formatVoiceNumber(prodMeters || 0)} मीटर प्रोडक्शन किया है।`);
                                    }}
                                    onBlur={(e) => handleTextBlurVoice(e.target.value, " प्रोडक्शन मीटर")}
                                    onChange={e=>setProdMeters(e.target.value)} 
                                />
                                {isFetchingStats && <span style={{ position: 'absolute', right: '12px', fontSize: '8px', fontWeight: '700', color: '#3B82F6', background: '#EFF6FF', padding: '2px 6px', borderRadius: '4px' }}>Syncing...</span>}
                            </div>
                        </div>

                        <div>
                            <label style={styles.fieldLabel}>Rate / Meter</label>
                            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '38px', background: '#F8FAFC', borderRight: '1px solid #E2E8F0', borderRadius: '12px 0 0 12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <span style={{ fontSize: '14px', fontWeight: '800', color: '#64748B' }}>₹</span>
                                </div>
                                <input 
                                    id="input-rate"
                                    type="number" 
                                    style={{ ...styles.input, paddingLeft: '46px', height: '42px', borderRadius: '12px' }} 
                                    value={rate} 
                                    onFocus={() => handleFocusVoice(" यहाँ Meter का रेट भरें।")}
                                    onBlur={(e) => handleTextBlurVoice(e.target.value, "रेट")}
                                    onChange={e=>setRate(e.target.value)} 
                                />
                            </div>
                        </div>
                    </div>

                    <div style={{ background: '#FFF1F2', padding: '12px', borderRadius: '16px', border: '1px solid #FFE4E6' }}>
                        <label style={{ ...styles.fieldLabel, color: '#E11D48', marginBottom: '4px' }}>Less: Advance Recovery Deduction (₹)</label>
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '38px', background: '#FFE4E6', borderRight: '1px solid #FCA5A5', borderRadius: '12px 0 0 12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <MinusCircle size={15} color="#E11D48" strokeWidth={2.5} />
                            </div>
                            <input 
                                id="input-recovery"
                                type="number" 
                                style={{ ...styles.input, paddingLeft: '46px', height: '42px', borderRadius: '12px', border: '1px solid #FCA5A5', color: '#E11D48', background: '#FFFFFF', fontSize: '14px', fontWeight: '700' }} 
                                value={advanceDed} 
                                onFocus={() => handleFocusVoice(" यहाँ पुराना कितना Advance कट करना है वो रकम भरें।")}
                                onBlur={(e) => handleTextBlurVoice(e.target.value, " Recovery रकम")}
                                onChange={e=>setAdvanceDed(e.target.value)} 
                            />
                        </div>
                    </div>
                </div>
            </div>
        )}

        {/* === SUB-CARD 4: FINAL NET PAYABLE AMOUNT INVOICE CARD === */}
        <div style={{
            background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
            borderRadius: '20px',
            padding: '16px',
            boxShadow: '0 10px 24px -6px rgba(15, 23, 42, 0.25), inset 0 1px 1px rgba(255,255,255,0.15)',
            position: 'relative',
            overflow: 'hidden'
        }}>
            <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '120px', height: '120px', background: paymentType === 'advance' ? 'radial-gradient(circle, #EF4444 0%, transparent 70%)' : 'radial-gradient(circle, #10B981 0%, transparent 70%)', opacity: 0.2, filter: 'blur(25px)', borderRadius: '50%', zIndex: 0 }} />
            
            <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <span style={{ fontSize: '10px', color: '#94A3B8', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px' }}>
                        {paymentType === 'advance' ? 'Advance Amount Issued' : 'Net Salary Payable'}
                    </span>
                    <span style={{ fontSize: '8px', background: 'rgba(255,255,255,0.1)', color: '#FFFFFF', padding: '3px 8px', borderRadius: '6px', fontWeight: '700', letterSpacing: '0.5px' }}>
                        AUTO CALC
                    </span>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '24px', fontWeight: '800', color: paymentType === 'advance' ? '#FCA5A5' : '#6EE7B7' }}>₹</span>
                    <input 
                        id="input-amount"
                        type="number" 
                        style={{ 
                            width: '100%', border: 'none', background: 'transparent', outline: 'none', 
                            fontSize: '34px', fontWeight: '900', letterSpacing: '-1px',
                            color: '#FFFFFF', padding: 0
                        }} 
                        value={amount} 
                        onFocus={() => handleFocusVoice(paymentType === 'advance' ? " यहाँ वर्कर का एडवांस अमाउंट चेक करें।" : " यहाँ वर्कर की net सैलरी चेक करें।")}
                        onBlur={(e) => handleTextBlurVoice(e.target.value, " रकम")}
                        onChange={e => setAmount(e.target.value.replace(/^0+(?=\d)/, ''))} 
                        placeholder="0" 
                    />
                </div>
            </div>
        </div>

        {/* === SUB-CARD 5: AUDIT LOGS, DATE & TRANSACTION MODE META === */}
        <div style={{
            background: 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            padding: '16px',
            boxShadow: '0 6px 20px rgba(15, 23, 42, 0.02), inset 0 1px 2px rgba(255,255,255,0.8)',
            border: '1px solid rgba(15, 23, 42, 0.04)',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px'
        }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                    <label style={styles.fieldLabel}>Disbursal Date</label>
                    <div style={{ position: 'relative' }}>
                        <input 
                            id="input-date"
                            type="date" 
                            style={{ ...styles.input, height: '42px', borderRadius: '12px', paddingRight: '6px', background: '#FFFFFF' }} 
                            value={date} 
                            onFocus={() => handleFocusVoice(" यहाँ तारीख चुनें।")}
                            onChange={e=>setDate(e.target.value)} 
                        />
                    </div>
                </div>
                
                <div id="mode-selection-box">
                    <label style={styles.fieldLabel}>Payment Channel</label>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                            <Banknote size={15} color="#475569" />
                        </div>
                        <select 
                            id="select-mode"
                            style={{ ...styles.input, paddingLeft: '36px', height: '42px', borderRadius: '12px', appearance: 'none', WebkitAppearance: 'none', background: '#FFFFFF' }} 
                            value={baseMode} 
                            onFocus={() => handleFocusVoice(" यहाँ चुनें कि पैसे कैश दिए हैं या Online।")}
                            onChange={(e)=>{
                                const val = e.target.value;
                                setBaseMode(val);
                                if (voiceAssist && !activeGuideFlow) {
                                    if (val === 'cash') speakHinglish(" आपने कैश चुना है।");
                                    else speakHinglish(" आपने ऑनलाइन चुना है, ऐप सिलेक्ट करें।");
                                }
                            }}
                        >
                            <option value="cash">Liquid Cash</option>
                            <option value="online">Online Gateway</option>
                        </select>
                        <div style={{ position: 'absolute', top: '50%', right: '10px', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#64748B' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="m6 9 6 6 6-6"/></svg>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Online Banking/UPI Apps Container */}
            {baseMode === 'online' && (
                <div id="online-apps-box" style={{ animation: 'fadeIn 0.25s ease-out', background: '#F8FAFC', padding: '12px', borderRadius: '14px', border: '1px dashed #E2E8F0' }}>
                    <label style={{ ...styles.fieldLabel, marginBottom: '6px', color: '#0284C7' }}>Select Targeted UPI App Widget</label>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {['PhonePe', 'GPay', 'Paytm', 'BharatPe', 'Other'].map(app => {
                            let AppIcon = <CreditCard size={12} color={onlineApp === app ? '#FFFFFF' : '#475569'} />;
                            if (app === 'PhonePe') AppIcon = <img src="https://www.google.com/s2/favicons?domain=phonepe.com&sz=32" alt="" style={{ width: '13px', height: '13px', borderRadius: '3px' }} />;
                            else if (app === 'GPay') AppIcon = <img src="https://www.google.com/s2/favicons?domain=pay.google.com&sz=32" alt="" style={{ width: '13px', height: '13px', borderRadius: '3px' }} />;
                            else if (app === 'Paytm') AppIcon = <img src="https://www.google.com/s2/favicons?domain=paytm.com&sz=32" alt="" style={{ width: '13px', height: '13px', borderRadius: '3px' }} />;
                            else if (app === 'BharatPe') AppIcon = <img src="https://www.google.com/s2/favicons?domain=bharatpe.com&sz=32" alt="" style={{ width: '13px', height: '13px', borderRadius: '3px' }} />;

                            const isSelected = onlineApp === app;
                            return (
                                <button 
                                    key={app} 
                                    onClick={(e) => handleVoiceClick(e, `${app} ऐप का ऑप्शन है।`, () => setOnlineApp(app), 'app-' + app)} 
                                    style={{
                                        padding: '8px 12px', borderRadius: '10px', fontSize: '10px', cursor: 'pointer',
                                        border: isSelected ? '1px solid #0284C7' : '1px solid #E2E8F0',
                                        background: isSelected ? '#E0F2FE' : '#FFFFFF',
                                        color: isSelected ? '#0369A1' : '#475569',
                                        fontWeight: '700', transition: 'all 0.2s',
                                        display: 'flex', alignItems: 'center', gap: '6px',
                                        boxShadow: isSelected ? '0 4px 10px rgba(2, 132, 199, 0.08)' : 'none'
                                    }}
                                >
                                    {AppIcon}
                                    {app}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>

        {/* === MASTER TRIGGER EXECUTION BUTTON === */}
        <div style={{ width: '100%' }}>
            <Button 
                id="btn-save-payment" 
                onClick={(e) => handleVoiceClick(
                    e, 
                    paymentType === 'advance' 
                        ? " Advance Pay करने के लिए यहाँ दोबारा क्लिक करें।" 
                        : " Salary Pay करने के लिए यहाँ दोबारा क्लिक करें।", 
                    handlePaymentSave
                )} 
                style={{ 
                    background: paymentType === 'advance' ? 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)' : 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                    boxShadow: paymentType === 'advance' ? '0 10px 24px -4px rgba(220, 38, 38, 0.3)' : '0 10px 24px -4px rgba(16, 185, 129, 0.3)',
                    height: '48px',
                    borderRadius: '14px',
                    width: '100%'
                }}
            >
                <span className="force-white-btn-text" style={{ fontSize: '13px', fontWeight: '800', letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                    {paymentType === 'advance' ? 'DISBURSE ADVANCE AMOUNT' : 'EXECUTE SALARY DISBURSEMENT'}
                </span>
            </Button>
        </div>

    </div>
)}

            

{activeTab === 'history' && (
    <div>
        {/* 🌟 NEW ULTRA-PREMIUM UNIFIED GLASSMORPHISM OVERVIEW CONTAINER 🌟 */}
        <div className="premium-glass animate-in" style={{ 
            background: 'rgba(255, 255, 255, 0.45)', 
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.6)',
            borderRadius: '24px',
            padding: '16px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.03), inset 0 1px 0 rgba(255,255,255,1)',
            marginBottom: '16px'
        }}>
            {/* Row 1: Total Salary Paid Card (WorkerHome StatCard Style) */}
            <div 
                onClick={(e) => handleVoiceClick(e, ` वर्कर्स को टोटल सैलरी की रकम,  ${formatVoiceCurrency(stats.sTotal)} दिए गए हैं।`, () => setStatFilterType(statFilterType === 'salary' ? 'all' : 'salary'), 'filter-salary')}
                style={{
                    background: 'rgba(255, 255, 255, 0.95)', 
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    borderRadius: '20px', 
                    padding: '16px',
                    boxShadow: statFilterType === 'salary' ? '0 12px 28px rgba(16, 185, 129, 0.2), inset 0 1px 2px rgba(255,255,255,0.8)' : '0 8px 24px rgba(0,0,0,0.04), inset 0 1px 2px rgba(255,255,255,0.8)', 
                    border: statFilterType === 'salary' ? '2px solid #10B981' : '1px solid rgba(255,255,255,0.5)',
                    display: 'flex', flexDirection: 'column', gap: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    width: '100%',
                    overflow: 'hidden',
                    marginBottom: '12px'
                }}
            >
                {/* Decorative Glowing Radial Blur Background Circle */}
                <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '80px', height: '80px', background: 'radial-gradient(circle, #34D399 0%, transparent 70%)', opacity: 0.18, filter: 'blur(20px)', borderRadius: '50%', zIndex: 0 }} />

                {/* Top Row: Icon Badge & Label */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', zIndex: 1 }}>
                    <div style={{ 
                        background: 'linear-gradient(135deg, #059669, #10B981)', 
                        padding: '8px', 
                        borderRadius: '12px', 
                        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)', 
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff'
                    }}>
                        <Banknote size={20} strokeWidth={2} />
                    </div>
                    <span style={{ fontSize: '10px', color: '#64748B', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                        Total Salary Paid
                    </span>
                </div>

                {/* Bottom Row: Large Master Value */}
                <div style={{ zIndex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                        <span style={{ fontSize: '28px', fontWeight: '900', color: '#0F172A', letterSpacing: '-1px' }}>
                            {formatCurrency(stats.sTotal)}
                        </span>
                    </div>
                </div>
            </div>

            {/* Row 2: Advance & Recovered Split Cards Grid (WorkerHome StatCard Style) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                {/* Advance Given Card */}
                <div 
                    onClick={(e) => handleVoiceClick(e, ` वर्कर्स को टोटल एडवांस ${formatVoiceCurrency(stats.aTotal)} दिया गया है।`, () => setStatFilterType(statFilterType === 'advance' ? 'all' : 'advance'), 'filter-advance')}
                    style={{
                        background: 'rgba(255, 255, 255, 0.95)', 
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        borderRadius: '20px', 
                        padding: '16px',
                        boxShadow: statFilterType === 'advance' ? '0 12px 28px rgba(220, 38, 38, 0.15), inset 0 1px 2px rgba(255,255,255,0.8)' : '0 8px 24px rgba(0,0,0,0.04), inset 0 1px 2px rgba(255,255,255,0.8)', 
                        border: statFilterType === 'advance' ? '2px solid #D32F2F' : '1px solid rgba(255,255,255,0.5)',
                        display: 'flex', flexDirection: 'column', gap: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        width: '100%',
                        overflow: 'hidden'
                    }}
                >
                    <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '80px', height: '80px', background: 'radial-gradient(circle, #FCA5A5 0%, transparent 70%)', opacity: 0.18, filter: 'blur(20px)', borderRadius: '50%', zIndex: 0 }} />

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', zIndex: 1 }}>
                        <div style={{ 
                            background: 'linear-gradient(135deg, #DC2626, #FCA5A5)', 
                            padding: '8px', 
                            borderRadius: '12px', 
                            boxShadow: '0 4px 12px rgba(220, 38, 38, 0.25)', 
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff'
                        }}>
                            <Wallet size={16} />
                        </div>
                        <span style={{ fontSize: '10px', color: '#64748B', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                            Advances Given
                        </span>
                    </div>

                    <div style={{ zIndex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                            <span style={{ fontSize: '24px', fontWeight: '900', color: '#0F172A', letterSpacing: '-1px' }}>
                                {formatCurrency(stats.aTotal)}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Advances Received (Recovered) Card */}
                <div 
                    onClick={(e) => handleVoiceClick(e, ` Workers se टोटल Advance वापस ${formatVoiceCurrency(stats.rTotal)} रिकवर हुआ है।`, () => setStatFilterType(statFilterType === 'recovered' ? 'all' : 'recovered'), 'filter-recovered')}
                    style={{
                        background: 'rgba(255, 255, 255, 0.95)', 
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        borderRadius: '20px', 
                        padding: '16px',
                        boxShadow: statFilterType === 'recovered' ? '0 12px 28px rgba(59, 130, 246, 0.15), inset 0 1px 2px rgba(255,255,255,0.8)' : '0 8px 24px rgba(0,0,0,0.04), inset 0 1px 2px rgba(255,255,255,0.8)', 
                        border: statFilterType === 'recovered' ? '2px solid #3B82F6' : '1px solid rgba(255,255,255,0.5)',
                        display: 'flex', flexDirection: 'column', gap: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        width: '100%',
                        overflow: 'hidden'
                    }}
                >
                    <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '80px', height: '80px', background: 'radial-gradient(circle, #7DD3FC 0%, transparent 70%)', opacity: 0.18, filter: 'blur(20px)', borderRadius: '50%', zIndex: 0 }} />

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', zIndex: 1 }}>
                        <div style={{ 
                            background: 'linear-gradient(135deg, #3B82F6, #7DD3FC)', 
                            padding: '8px', 
                            borderRadius: '12px', 
                            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)', 
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff'
                        }}>
                            <RefreshCw size={16} />
                        </div>
                        <span style={{ fontSize: '10px', color: '#64748B', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                            Advances Received
                        </span>
                    </div>

                    <div style={{ zIndex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                            <span style={{ fontSize: '24px', fontWeight: '900', color: '#0F172A', letterSpacing: '-1px' }}>
                                {formatCurrency(stats.rTotal)}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

                         {/* 🌟 PREMIUM FILTER & DATE SEARCH BOX 🌟 */}
                         <div style={{ marginBottom: '16px' }}>
                             <div style={{ 
                                 background: 'linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.65) 100%)', 
                                 backdropFilter: 'blur(24px)', 
                                 borderRadius: '20px', 
                                 padding: '12px', 
                                 boxShadow: '0 10px 25px rgba(0,0,0,0.04), inset 0 2px 0 rgba(255,255,255,1)', 
                                 border: '1px solid rgba(255,255,255,0.7)' 
                             }}>
                                 {/* 1. Worker Dropdown Filter */}
                                 <div style={{display:'flex', alignItems:'center', gap:'8px', background:'#fff', padding:'8px 12px', borderRadius:'12px', border:'1px solid rgba(0,0,0,0.04)', boxShadow:'0 2px 8px rgba(0,0,0,0.03)', marginBottom: '8px'}}>
                                     <div style={{ background: '#f1f5f9', padding: '6px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                         <Filter size={14} color="#64748B"/>
                                     </div>
                                     <select 
                                         style={{border:'none', outline:'none', fontSize:'11px', color:'#0F172A', fontWeight:'600', background:'transparent', width:'100%', cursor: 'pointer'}} 
                                         value={historyFilterId} 
                                         onFocus={() => handleFocusVoice(" जिस वर्कर का Salary रिकॉर्ड देखना है, उस वर्कर को चुनें।")}
                                         onChange={(e)=>{
                                             setHistoryFilterId(e.target.value);
                                             const wName = e.target.options[e.target.selectedIndex].text;
                                             if(handleSelectVoice) handleSelectVoice(wName, "फ़िल्टर वर्कर");
                                         }}
                                     >
                                         <option value="">All Workers (Sabhi Workers)</option>
                                         {workers && workers.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                                     </select>
                                 </div>

                                 {/* 2. Date Search Strip */}
                                 {!isSearchExpanded && !startDate && !endDate ? (
                                     <div onClick={(e) => { handleVoiceClick(e, " डेट के हिसाब से पेमेंट हिस्ट्री सर्च करने के लिए इस सर्च बॉक्स पर क्लिक करें।", () => setIsSearchExpanded(true), 'btn-search'); }} 
                                         style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', borderRadius: '12px', padding: '10px 14px', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.04)', transition: 'all 0.2s', marginBottom: '0px' }}>
                                         <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                             <Search size={14} color="#64748B"/>
                                             <span style={{ fontSize: '10px', color: '#64748B', fontWeight: '500' }}>Search by Date...</span>
                                         </div>
                                         <div style={{display:'flex', color: '#94A3B8'}}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                                         </div>
                                     </div>
                                 ) : (
                                     <div style={{ background: '#fff', borderRadius: '14px', padding: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.04)', animation: 'slideDown 0.2s ease-out', marginBottom: '0px' }}>
                                         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                             <span style={{ fontSize: '10px', fontWeight: '600', color: '#475569', letterSpacing: '0.5px' }}>DATE RANGE</span>
                                             <button onClick={(e) => { handleVoiceClick(e, " सर्च फिल्टर हटाने के लिए इस पर दोबारा क्लिक करें।", () => {setStartDate(''); setEndDate(''); setIsSearchExpanded(false);}, 'btn-clear'); }} 
                                                 style={{ background: 'rgba(239, 68, 68, 0.1)', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px', display: 'flex', alignItems: 'center', color: '#DC2626', transition: 'background 0.2s' }}>
                                                 <X size={12} /> <span style={{ fontSize: '8px', fontWeight: '600', marginLeft: '2px' }}>CLEAR</span>
                                             </button>
                                         </div>
                                         <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                                             <input type="date" value={startDate} onClick={(e) => handleVoiceClick(e, " किस दिन का पेमेंट रिकॉर्ड देखना चाहते है, उस दिन की तारीख चुनें।", null)} onChange={(e) => setStartDate(e.target.value)} 
                                                 style={{ flex: 1, padding: '8px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '11px', outline: 'none', background: '#F8FAFC', color: '#0F172A', fontWeight: '500' }} />
                                             <span style={{ color: '#94A3B8', fontWeight: '600' }}>-</span>
                                             <input type="date" value={endDate} onClick={(e) => handleVoiceClick(e, " किस दिन तक का पेमेंट रिकॉर्ड देखना चाहते है, उस दिन की तारीख चुनें।", null)} onChange={(e) => setEndDate(e.target.value)} 
                                                 style={{ flex: 1, padding: '8px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '11px', outline: 'none', background: '#F8FAFC', color: '#0F172A', fontWeight: '500' }} />
                                         </div>
                                     </div>
                                 )}
                             </div>
                         </div>

                    {/* PREMIUM TRANSACTION HISTORY - WORKER PAYMENT STYLE UNIFIED GLASSMORPHISM BOX */}
<div className="premium-glass animate-in" style={{ 
    animationDelay: '0.2s', 
    padding: '0', 
    overflow: 'visible', // Dropdown menus cut hone se rokne ke liye visible rakha hain
    background: 'rgba(255, 255, 255, 0.45)', 
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border: '1px solid rgba(255, 255, 255, 0.6)',
    borderRadius: '24px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.03), inset 0 1px 0 rgba(255,255,255,1)',
    marginBottom: '16px'
}}>
    
    {/* Section Modern Header Strip - Inside Glass Box */}
    <div style={{ 
        padding: '20px', 
        background: 'rgba(255, 255, 255, 0.5)', 
        borderBottom: '1px solid rgba(226, 232, 240, 0.8)', 
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderTopLeftRadius: '24px', borderTopRightRadius: '24px'
    }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ background: '#1E293B', padding: '6px', borderRadius: '10px', boxShadow: '0 2px 6px rgba(15, 23, 42, 0.15)' }}>
                    <Clock size={16} color="#FFFFFF" />
                </div>
                <span style={{ fontSize: '14px', fontWeight: '800', color: '#0F172A', letterSpacing: '0.3px' }}>Transaction History</span>
            </div>
        </div>

        {statFilterType !== 'all' && (
            <div style={{ background: '#FFFFFF', borderRadius: '10px', padding: '6px 12px', boxShadow: '0 4px 12px rgba(0,0,0,0.04)', border: '1px solid #F1F5F9', display: 'flex', alignItems: 'center' }}>
                <span style={{ fontSize: '9px', color: '#0F172A', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {statFilterType}
                </span>
            </div>
        )}
    </div>
    
    {/* Floating Cards Area Container - Soft Translucent Base */}
    <div style={{ padding: '16px', background: 'rgba(248, 250, 252, 0.3)', minHeight: '200px', borderBottomLeftRadius: '24px', borderBottomRightRadius: '24px' }}>
        {isLoadingHistory ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <p style={{ color: '#64748B', fontSize: '12px', fontWeight: '600' }}>Fetching history records...</p>
            </div>
        ) : filteredHistory.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', background: '#ffffff', borderRadius: '20px', border: '1px solid #E2E8F0' }}>
                <div style={{ background: '#F8FAFC', padding: '16px', borderRadius: '50%', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
                    <AlertTriangle size={28} color="#94A3B8" />
                </div>
                <p style={{ color: '#64748B', fontSize: '12px', fontWeight: '600', margin: 0 }}>No history records found.</p>
            </div>
        ) : (
            filteredHistory.map((p, i) => {
                const isRecoveredFilter = statFilterType === 'recovered';
                const mainOpacity = isRecoveredFilter ? 0.3 : 1;
                
                const gross = parseFloat(p.amount) + parseFloat(p.advance_adjusted);
                const rec = parseFloat(p.advance_adjusted);
                const paid = parseFloat(p.amount);
                const isSalary = p.type === 'salary';

                const isLastItem = i >= filteredHistory.length - 2 && filteredHistory.length > 3;
                const dropdownStyle = isLastItem 
                    ? { ...styles.menuDropdown, bottom: '100%', top: 'auto', marginBottom: '6px' } 
                    : { ...styles.menuDropdown, top: '100%', bottom: 'auto', marginTop: '6px' };

                return (
                    <div 
                        id={i === 0 ? 'history-item-0' : `history-item-${i}`}
                        key={p.id || i} 
                        onClick={(e) => handleVoiceClick(e, getTransactionVoiceMsg(p), () => handleViewClick(p), `hist-${p.id}`)} 
                        style={{
                            background: '#ffffff', 
                            borderRadius: '16px', 
                            padding: '16px 16px 16px 20px', 
                            boxShadow: '0 6px 18px rgba(15,23,42,0.03)', 
                            border: '1px solid #E2E8F0', 
                            display: 'flex', 
                            flexDirection: 'column', 
                            cursor: 'pointer',
                            transition: 'all 0.25s ease', 
                            marginBottom: '12px', 
                            position: 'relative', 
                            overflow: 'visible', // Action dropdown display ke liye visible zuri h
                            zIndex: menuOpenId === p.id ? 50 : 1
                        }}
                    >
                        {/* 🌟 Left Premium Color Strip Indicator (Curved edges to match card shape) 🌟 */}
                        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '6px', background: isSalary ? '#10B981' : '#DC2626', borderTopLeftRadius: '16px', borderBottomLeftRadius: '16px' }} />
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                            <div style={{ flex: 1, paddingRight: '8px' }}>
                                <div style={{ opacity: mainOpacity, transition: 'opacity 0.2s' }}>
                                    
                                    {/* Worker Name Profile Header */}
                                    <div style={{ fontSize: '14px', fontWeight: '800', color: '#0F172A', textTransform: 'capitalize', letterSpacing: '-0.3px', marginBottom: '2px' }}>
                                        {p.worker_name}
                                    </div>

                                    {/* Transaction Type Subtitle & Edited Badge */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                        <span style={{ fontSize: '11px', fontWeight: '700', color: isSalary ? '#059669' : '#D32F2F' }}>
                                            {isSalary ? (p.salary_period ? `${p.salary_period} Salary` : 'Salary') : 'Advance'}
                                        </span>
                                        {p.is_edited == 1 && (
                                            <span style={{ fontSize: '8px', background: '#FEF2F2', color: '#DC2626', padding: '2px 6px', borderRadius: '6px', fontWeight: '800', letterSpacing: '0.5px' }}>
                                                EDITED
                                            </span>
                                        )}
                                    </div>
                                    
                                    {/* Date & Mode Parameters Strip */}
                                    <div style={{ fontSize: '10px', color: '#64748B', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <CalIcon size={11} color="#94A3B8" /> {formatDateTime(p.date)}
                                        </span>
                                        <span style={{ color: '#E2E8F0' }}>•</span>
                                        {(() => {
                                            const mStr = (p.mode || '').toLowerCase();
                                            let mName = p.mode; let mColor = '#0F172A'; let mIcon = null; 
                                            
                                            if (mStr === 'cash') {
                                                mName = 'Cash'; mColor = '#10B981';
                                                mIcon = <Banknote size={11} color={mColor} />;
                                            } else if (mStr.includes('phonepe')) {
                                                mName = 'PhonePe'; mColor = '#0F172A';
                                                mIcon = <img src="https://www.google.com/s2/favicons?domain=phonepe.com&sz=32" alt="PhonePe" style={{ width: '11px', height: '11px', borderRadius: '2px' }} />;
                                            } else if (mStr.includes('gpay') || mStr.includes('google')) {
                                                mName = 'GPay'; mColor = '#0F172A';
                                                mIcon = <img src="https://www.google.com/s2/favicons?domain=pay.google.com&sz=32" alt="GPay" style={{ width: '11px', height: '11px', borderRadius: '2px' }} />;
                                            } else if (mStr.includes('paytm')) {
                                                mName = 'Paytm'; mColor = '#0F172A';
                                                mIcon = <img src="https://www.google.com/s2/favicons?domain=paytm.com&sz=32" alt="Paytm" style={{ width: '11px', height: '11px', borderRadius: '2px' }} />;
                                            } else if (mStr.includes('bharatpe')) {
                                                mName = 'BharatPe'; mColor = '#0F172A';
                                                mIcon = <img src="https://www.google.com/s2/favicons?domain=bharatpe.com&sz=32" alt="BharatPe" style={{ width: '11px', height: '11px', borderRadius: '2px' }} />;
                                            } else if (mStr.includes('online')) {
                                                mName = 'Online'; mColor = '#3B82F6';
                                                mIcon = <CreditCard size={11} color={mColor} />;
                                            }

                                            return (
                                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                    {mIcon}
                                                    <span style={{ color: mColor, fontWeight: '700', fontSize: '10px', textTransform: 'capitalize' }}>{mName}</span>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                                
                               {/* 🌟 Executive Calculations Breakdown Bubble (10% Smaller & Standard Terminology) 🌟 */}
                                {isSalary && rec > 0 && (
                                    <div style={{
                                        fontSize: '10px', color: '#0F172A', marginTop: '10px', 
                                        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '5px',
                                        background: '#F8FAFC', padding: '4px 8px', borderRadius: '6px',
                                        border: '1px solid #E2E8F0', opacity: 1, width: 'fit-content'
                                    }}>
                                        <span className="force-blue" style={{ fontWeight: '700', fontSize: '10px' }}>Gross: ₹{Number(gross.toFixed(0))}</span>
                                        <span style={{ color: '#94A3B8', fontWeight: '700', fontSize: '10px' }}>|</span>
                                        <span className="force-red" style={{ fontWeight: '700', fontSize: '10px' }}>Deduction: ₹{Number(rec.toFixed(0))}</span>
                                        <span style={{ color: '#94A3B8', fontWeight: '700', fontSize: '10px' }}>=</span>
                                        <span className="force-green" style={{ fontWeight: '700', fontSize: '10px' }}>Net Paid: ₹{Number(paid.toFixed(0))}</span>
                                    </div>
                                )}
                            </div>
                            
                            {/* Right Side Stack: Amount Values Only */}
                            <div style={{ display: 'flex', alignItems: 'center', minHeight: '52px' }}>
                                <div style={{ textAlign: 'right', minWidth: '75px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                        <span className={p.type === 'advance' ? 'force-red' : 'force-green'} style={{ fontSize: '18px', fontWeight: '900', letterSpacing: '-0.5px' }}>
                                            {formatCurrency(p.amount).replace('Rs. ', '₹')}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: '9px', textTransform: 'uppercase', fontWeight: '700', marginTop: '2px', color: p.type === 'advance' ? '#D32F2F' : '#64748B', letterSpacing: '0.3px' }}>
                                        {isSalary ? 'Net Paid' : 'Given'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })
        )}
    </div>
</div>
                    </div>
                )}
            </div>

          {/* ===== 🌟 PREMIUM SMART GUIDE MODAL 🌟 ===== */}
      <Modal
          isOpen={showGuideModal}
          onClose={() => {
              if (window.speechSynthesis) {
                  window.speechSynthesis.pause();
                  window.speechSynthesis.cancel();
              }
              setShowGuideModal(false);
          }}
          title="Smart Live Guide"
          icon={HelpCircle}
          maxWidth="430px"
      >
          <div>
              <p style={{fontSize:'8px', fontWeight:'600', color:'#64748B', marginBottom:'16px', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 0}}>
                  Aap kis topic ke bare me sikhna chahte hain? Niche Diye Gaye Options Me Se Chune.
              </p>
              
              <div style={{display:'flex', flexDirection:'column', gap:'12px'}}>
                  
                  {/* 1. Worker Ko Salary Payment */}
                  <div onClick={() => {
                      setShowGuideModal(false);
                      startSmartGuide('salary_payment');
                      if (activeTab === 'new') {
                          setTimeout(() => {
                              const target = document.querySelector('.smart-guide-target');
                              if (target && target.id === 'tab-new') advanceNextStep(true);
                          }, 400); 
                      }
                  }} style={{display: 'flex', justifyContent: 'space-between', padding: '10px', alignItems: 'center', background:'#f8fafc', borderRadius:'16px', cursor:'pointer', border:'1px solid #e2e8f0', transition: 'all 0.2s'}}>
                      <span style={{fontSize:'10px', fontWeight:'700', color:'#0F172A', paddingRight: '8px'}}>1. Worker Ko Salary Payment Kaise De?</span>
                      <div style={{ background: 'linear-gradient(135deg, #E0E7FF, #A5B4FC)', padding: '6px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(79, 70, 229, 0.15)', flexShrink: 0 }}>
                          <PlayCircle size={14} color="#4338CA"/>
                      </div>
                  </div>

                  {/* 2. Record Report */}
                  <div onClick={() => {
                      setShowGuideModal(false);
                      startSmartGuide('payment_report');
                      if (activeTab === 'history') {
                          setTimeout(() => {
                              const target = document.querySelector('.smart-guide-target');
                              if (target && target.id === 'tab-history') advanceNextStep(true);
                          }, 400);
                      }
                  }} style={{display: 'flex', justifyContent: 'space-between', padding: '10px', alignItems: 'center', background:'#f8fafc', borderRadius:'16px', cursor:'pointer', border:'1px solid #e2e8f0', transition: 'all 0.2s'}}>
                      <span style={{fontSize:'10px', fontWeight:'700', color:'#0F172A', paddingRight: '8px'}}>2. Salary/Advance PDF Report Kaise Download Kare?</span>
                      <div style={{ background: 'linear-gradient(135deg, #E0E7FF, #A5B4FC)', padding: '6px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(79, 70, 229, 0.15)', flexShrink: 0 }}>
                          <PlayCircle size={14} color="#4338CA"/>
                      </div>
                  </div>

                  {/* 3. Share Receipt */}
                  <div onClick={() => {
                      setShowGuideModal(false);
                      startSmartGuide('share_receipt');
                      if (activeTab === 'history') {
                          setTimeout(() => {
                              const target = document.querySelector('.smart-guide-target');
                              if (target && target.id === 'tab-history') advanceNextStep(true);
                          }, 400);
                      }
                  }} style={{display: 'flex', justifyContent: 'space-between', padding: '10px', alignItems: 'center', background:'#f8fafc', borderRadius:'16px', cursor:'pointer', border:'1px solid #e2e8f0', transition: 'all 0.2s'}}>
                      <span style={{fontSize:'10px', fontWeight:'700', color:'#0F172A', paddingRight: '8px'}}>3. Payment Record Ki Receipt Kaise Share Kare?</span>
                      <div style={{ background: 'linear-gradient(135deg, #E0E7FF, #A5B4FC)', padding: '6px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(79, 70, 229, 0.15)', flexShrink: 0 }}>
                          <PlayCircle size={14} color="#4338CA"/>
                      </div>
                  </div>

                  {/* 4. Edit Payment */}
                  <div onClick={() => {
                      setShowGuideModal(false);
                      startSmartGuide('edit_payment');
                      if (activeTab === 'history') {
                          setTimeout(() => {
                              const target = document.querySelector('.smart-guide-target');
                              if (target && target.id === 'tab-history') advanceNextStep(true);
                          }, 400);
                      }
                  }} style={{display: 'flex', justifyContent: 'space-between', padding: '10px', alignItems: 'center', background:'#f8fafc', borderRadius:'16px', cursor:'pointer', border:'1px solid #e2e8f0', transition: 'all 0.2s'}}>
                      <span style={{fontSize:'10px', fontWeight:'700', color:'#0F172A', paddingRight: '8px'}}>4. Payment Record Ko Edit Kaise Kare?</span>
                      <div style={{ background: 'linear-gradient(135deg, #E0E7FF, #A5B4FC)', padding: '6px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(79, 70, 229, 0.15)', flexShrink: 0 }}>
                          <PlayCircle size={14} color="#4338CA"/>
                      </div>
                  </div>

                  {/* 5. Advance Payment */}
                  <div onClick={() => {
                      setShowGuideModal(false);
                      startSmartGuide('advance_payment');
                      if (activeTab === 'new') {
                          setTimeout(() => {
                              const target = document.querySelector('.smart-guide-target');
                              if (target && target.id === 'tab-new') advanceNextStep(true);
                          }, 400);
                      }
                  }} style={{display: 'flex', justifyContent: 'space-between', padding: '10px', alignItems: 'center', background:'#f8fafc', borderRadius:'14px', cursor:'pointer', border:'1px solid #e2e8f0', transition: 'all 0.2s'}}>
                      <span style={{fontSize:'10px', fontWeight:'700', color:'#0F172A', paddingRight: '8px'}}>5. Worker Ko Advance Payment Kaise De?</span>
                      <div style={{ background: 'linear-gradient(135deg, #E0E7FF, #A5B4FC)', padding: '6px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(79, 70, 229, 0.15)', flexShrink: 0 }}>
                          <PlayCircle size={14} color="#4338CA"/>
                      </div>
                  </div>

              </div>
          </div>
      </Modal>

           {/* --- ALERT MODAL --- */}
            <Modal
                isOpen={alertConfig.show}
                onClose={closeAlert}
                title={alertConfig.title}
                icon={alertConfig.type === 'error' ? AlertTriangle : Info}
                iconColor={alertConfig.type === 'error' ? '#DC2626' : '#0284C7'}
                iconBg={alertConfig.type === 'error' ? 'linear-gradient(135deg, #FEE2E2, #FCA5A5)' : 'linear-gradient(135deg, #E0F2FE, #7DD3FC)'}
                maxWidth="340px"
            >
                <div style={{ textAlign: 'center', padding: '10px 0' }}>
                    <p style={{ fontSize: '11px', color: '#475569', margin: '0 0 20px 0', fontWeight: '500', lineHeight: '1.5' }}>{alertConfig.msg}</p>
                    <Button onClick={closeAlert} style={{ background: alertConfig.type === 'error' ? '#DC2626' : '#0F172A' }}>
                        Okay
                    </Button>
                </div>
            </Modal>

            {/* --- PREMIUM CONFIRM MODAL --- */}
            <ConfirmModal 
                isOpen={confirmConfig.show} 
                onClose={() => handleVoiceClick(null, " यह कैंसिल बटन है।", closeConfirm, 'btn-cancel-action')} 
                onConfirm={() => handleVoiceClick(null, " यह कन्फर्म बटन है।", handleConfirmAction, 'btn-confirm-action')} 
                message={confirmConfig.msg}
                title="Confirm Action"
                isDanger={true}
                confirmText="Confirm"
            />

        {/* ===== 🌟 EXECUTIVE CLASSIC UI VIEW MODAL (OPTIMIZED SCROLL FITTING) 🌟 ===== */}
         <Modal
             isOpen={showViewModal && !!viewData}
             onClose={closeModalsVoice}
             title="Transaction Receipt"
             icon={viewData?.type === 'salary' ? CheckCircle : Wallet}
             iconColor="#0F172A"
             iconBg="#F1F5F9"
             maxWidth="420px"
         >
             {viewData && (
                 <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '10px', background: 'transparent', textAlign: 'center' }}>
                     
                     {/* 💳 Minimalist Classic Header Statement Panel - Compact Padding */}
                     <div style={{ padding: '0px 0 2px 0' }}>
                         {/* Solid Classic Status Circle */}
                         <div style={{ 
                             width: '40px', height: '40px', borderRadius: '50%', 
                             background: viewData.type === 'salary' ? '#E6F4EA' : '#FCE8E6',
                             display: 'flex', alignItems: 'center', justifyContent: 'center', 
                             margin: '0 auto 8px auto', border: viewData.type === 'salary' ? '1px solid #A7F3D0' : '1px solid #FCA5A5'
                         }}>
                             {viewData.type === 'salary' ? (
                                 <Check size={18} color="#137333" strokeWidth={3} />
                             ) : (
                                 <Coins size={16} color="#C5221F" strokeWidth={2.5} />
                             )}
                         </div>

                         <span style={{ fontSize: '10px', color: '#64748B', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1.2px', display: 'block', marginBottom: '2px' }}>
                             {viewData.type === 'salary' ? 'Salary Disbursed Statement' : 'Advance Issued Statement'}
                         </span>
                         
                         <h2 style={{ fontSize: '32px', fontWeight: '800', color: '#0F172A', margin: 0, letterSpacing: '-1px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                             ₹{(Number(viewData.amount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                         </h2>
                     </div>

                     {/* 📊 CLASSIC ACCOUNTING LEDGER MATRIX (Streamlined Vertical Balance Sheet) */}
                     {viewData.type === 'salary' && parseFloat(viewData.advance_adjusted) > 0 && (
                         <div style={{ 
                             background: '#F8FAFC', 
                             borderRadius: '14px', 
                             padding: '12px 14px', 
                             border: '1px solid #E2E8F0',
                             width: '100%',
                             textAlign: 'left'
                         }}>
                             <div style={{ fontSize: '9px', color: '#64748B', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px', borderBottom: '1px solid #E2E8F0', paddingBottom: '4px' }}>
                                 Breakdown Ledger
                             </div>
                             <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                     <span style={{ fontSize: '11px', color: '#475569', fontWeight: '500' }}>Gross Earned Salary</span>
                                     <span style={{ fontSize: '11px', color: '#0F172A', fontWeight: '700' }}>
                                         ₹{Number((parseFloat(viewData.amount) + parseFloat(viewData.advance_adjusted)).toFixed(2)).toLocaleString('en-IN')}
                                     </span>
                                 </div>
                                 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                     <span style={{ fontSize: '11px', color: '#EF4444', fontWeight: '600' }}>Less: Advance Deduction</span>
                                     <span style={{ fontSize: '11px', color: '#EF4444', fontWeight: '700' }}>
                                         - ₹{Number(parseFloat(viewData.advance_adjusted).toFixed(2)).toLocaleString('en-IN')}
                                     </span>
                                 </div>
                                 <div style={{ height: '1px', background: '#E2E8F0', margin: '2px 0' }} />
                                 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                     <span style={{ fontSize: '11px', color: '#0F172A', fontWeight: '800' }}>Net Payable Disbursed</span>
                                     <span style={{ fontSize: '13px', color: '#10B981', fontWeight: '900' }}>
                                         ₹{Number(parseFloat(viewData.amount).toFixed(2)).toLocaleString('en-IN')}
                                     </span>
                                 </div>
                             </div>
                         </div>
                     )}
                     
                     {/* 📋 SYMMETRICAL REVENUE MANIFEST MATRIX - Tight Paddings */}
                     <div style={{ 
                         background: '#FFFFFF', 
                         borderRadius: '14px', 
                         padding: '2px 14px', 
                         border: '1px solid #E2E8F0', 
                         textAlign: 'left',
                         display: 'flex',
                         flexDirection: 'column',
                         width: '100%'
                     }}>
                         {/* Core Item: Registered Worker */}
                         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #F1F5F9' }}>
                             <span style={{ fontSize: '11px', color: '#64748B', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                 <UserIcon size={12} color="#94A3B8" /> Worker Name
                             </span>
                             <span style={{ fontSize: '12px', fontWeight: '700', color: '#0F172A', textTransform: 'capitalize' }}>
                                 {viewData.worker_name}
                             </span>
                         </div>
                         
                         {/* Core Item: Timeline Timestamp */}
                         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #F1F5F9' }}>
                             <span style={{ fontSize: '11px', color: '#64748B', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                 <CalIcon size={12} color="#94A3B8" /> Date & Time
                             </span>
                             <span style={{ fontSize: '12px', fontWeight: '600', color: '#334155' }}>
                                 {formatDateTime(viewData.date)}
                             </span>
                         </div>
                         
                         {/* Core Item: Financial Route Mode */}
                         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' }}>
                             <span style={{ fontSize: '11px', color: '#64748B', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                 <Banknote size={12} color="#94A3B8" /> Payment Mode
                             </span>
                             <span style={{ fontSize: '12px', fontWeight: '700', color: '#0F172A' }}>
                                 {(() => {
                                     const mStr = (viewData.mode || '').toLowerCase();
                                     let mName = viewData.mode;
                                     let mIcon = null;
                                     
                                     if (mStr === 'cash') {
                                         mName = 'Cash';
                                         mIcon = <Banknote size={12} color="#10B981" style={{ marginRight: '6px' }} />;
                                     } else if (mStr.includes('phonepe')) {
                                         mName = 'PhonePe';
                                         mIcon = <img src="https://www.google.com/s2/favicons?domain=phonepe.com&sz=32" alt="" style={{ width: '12px', height: '12px', borderRadius: '3px', marginRight: '6px', objectFit: 'contain' }} />;
                                     } else if (mStr.includes('gpay')) {
                                         mName = 'GPay';
                                         mIcon = <img src="https://www.google.com/s2/favicons?domain=pay.google.com&sz=32" alt="" style={{ width: '12px', height: '12px', borderRadius: '3px', marginRight: '6px', objectFit: 'contain' }} />;
                                     } else if (mStr.includes('paytm')) {
                                         mName = 'Paytm';
                                         mIcon = <img src="https://www.google.com/s2/favicons?domain=paytm.com&sz=32" alt="" style={{ width: '12px', height: '12px', borderRadius: '3px', marginRight: '6px', objectFit: 'contain' }} />;
                                     } else if (mStr.includes('bharatpe')) {
                                         mName = 'BharatPe';
                                         mIcon = <img src="https://www.google.com/s2/favicons?domain=bharatpe.com&sz=32" alt="" style={{ width: '12px', height: '12px', borderRadius: '3px', marginRight: '6px', objectFit: 'contain' }} />;
                                     } else if (mStr.includes('online')) {
                                         mName = 'Online Transfer';
                                         mIcon = <CreditCard size={12} color="#3B82F6" style={{ marginRight: '6px' }} />;
                                     }

                                     return (
                                         <div style={{ display: 'inline-flex', alignItems: 'center', textTransform: 'capitalize' }}>
                                             {mIcon} <span>{mName}</span>
                                         </div>
                                     );
                                 })()}
                             </span>
                         </div>
                     </div>

                     {/* 🗓️ PRODUCTION INTERVAL METADATA BLOCK - Slim Bounds */}
                     {((viewData.prod_start_date && viewData.prod_start_date !== '0000-00-00') || viewData.type === 'salary') && (
                         <div style={{ 
                             background: '#F8FAFC', 
                             borderRadius: '12px', 
                             padding: '10px 14px', 
                             border: '1px solid #E2E8F0', 
                             display: 'flex', 
                             justifyContent: 'space-between', 
                             alignItems: 'center',
                             width: '100%',
                             textAlign: 'left'
                         }}>
                             <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                                 <span style={{ fontSize: '9px', color: '#64748B', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                     Accounting Cycle Duration
                                 </span>
                                 <span style={{ fontSize: '11px', color: '#0F172A', fontWeight: '700' }}>
                                     {(viewData.prod_start_date && viewData.prod_start_date !== '0000-00-00') ? `${formatDate(viewData.prod_start_date)} – ${formatDate(viewData.prod_end_date)}` : 'Cycle Bounds Empty'}
                                 </span>
                             </div>
                             {viewData.type === 'salary' && (
                                 <span style={{ 
                                     background: '#FFFFFF', color: '#475569', 
                                     padding: '3px 8px', borderRadius: '6px', 
                                     fontSize: '9px', fontWeight: '800', 
                                     textTransform: 'uppercase', letterSpacing: '0.5px', 
                                     border: '1px solid #D1D5DB'
                                 }}>
                                     {viewData.salary_period || 'Weekly'}
                                 </span>
                             )}
                         </div>
                     )}

                     {viewData.remarks && (
                         <div style={{ 
                             padding: '10px 12px', 
                             borderRadius: '10px', 
                             borderLeft: '3px solid #64748B',
                             background: '#F8FAFC',
                             textAlign: 'left',
                             width: '100%'
                         }}>
                             <span style={{ fontSize: '9px', color: '#64748B', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '1px' }}>Statement Note</span>
                             <p style={{ margin: 0, fontSize: '11px', color: '#475569', fontWeight: '500', fontStyle: 'italic' }}>"{viewData.remarks}"</p>
                         </div>
                     )}
                     
                    {/* 🌟 PRIVILEGED EXECUTIVE ACTION ROW BUTTONS - SCRIPTED WITH AUTOMATED HISTORY NAV ROUTE */}
                     <div style={{ display: 'flex', gap: '12px', marginTop: '4px', width: '100%' }}>
                         <button 
                             id="btn-view-history" 
                             onClick={(e) => handleVoiceClick(
                                 e, 
                                 ` इस वर्कर की पूरी पेमेंट हिस्ट्री और पुरानी रिपोर्ट्स देखने के लिए दोबारा क्लिक करें।`, 
                                 () => { 
                                     if(window.speechSynthesis) window.speechSynthesis.cancel(); 
                                     setHistoryFilterId(viewData.worker_id); // Target specifically this worker id filter
                                     setActiveTab('history'); // Redirect interface route to history dashboard
                                     fetchData(); // Sync live dataset from endpoint
                                     setShowViewModal(false); // Close current receipt viewport smoothly
                                 }, 
                                 'btn-view-history'
                             )}
                             style={{
                                 flex: 1, padding: '11px 16px', borderRadius: '12px', border: '1px solid #CBD5E1',
                                 background: '#FFFFFF', color: '#0F172A', fontWeight: '700', fontSize: '13px', 
                                 cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'all 0.2s'
                             }}
                             className="micro-click-pill"
                         >
                             <Clock size={14} color="#0F172A" strokeWidth={2.5} /> View History
                         </button>
                         <button 
                             id="btn-share-receipt" 
                             onClick={(e) => handleVoiceClick(e, " यह शेयर रसीद बटन है।", () => handleWhatsAppShare(viewData), 'btn-share-receipt')}
                             style={{
                                 flex: 1, padding: '11px 16px', borderRadius: '12px', border: 'none',
                                 background: '#0F172A', color: '#FFFFFF', fontWeight: '700', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)'
                             }}
                             className="micro-click-pill"
                         >
                             <MessageCircle size={14} color="#FFFFFF" strokeWidth={2.5} /> Share Receipt
                         </button>
                     </div>
                 </div>
             )}
         </Modal>

      {/* ===== 🌟 HIGH-END SMART EDIT MODAL REVAMP 🌟 ===== */}
        <Modal
            isOpen={showEditModal && !!editData}
            onClose={closeModalsVoice}
            title="Edit Transaction"
            icon={Edit}
            iconColor="#0F172A"
            iconBg="#F1F5F9"
            maxWidth="420px"
        >
            {editData && (
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '12px', background: 'transparent', textAlign: 'left' }}>
                    
                    {/* ⚙️ Row Group 1: Transaction Type Dropdown Card */}
                    <div style={{ background: '#F8FAFC', padding: '10px 14px', borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                        <label style={{ fontSize: '10px', fontWeight: '800', color: '#64748B', textTransform: 'uppercase', marginBottom: '4px', display: 'block', letterSpacing: '0.6px' }}>
                            Transaction Type
                        </label>
                        <select 
                            id="edit-type" 
                            style={{ width: '100%', border: 'none', outline: 'none', background: 'transparent', fontSize: '12px', fontWeight: '700', color: '#0F172A', cursor: 'pointer', padding: '2px 0' }} 
                            value={editData.type} 
                            onFocus={() => handleFocusVoice(" यहाँ ट्रांज़ैक्शन का Type Change करें।")} 
                            onChange={e => setEditData({ ...editData, type: e.target.value })}
                        >
                            <option value="salary">Salary Payment</option>
                            <option value="advance">Give Advance</option>
                        </select>
                    </div>

                    {/* ⚙️ Row Group 2: Amount Entry Box Card */}
                    <div style={{ background: '#F8FAFC', padding: '10px 14px', borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                        <label style={{ fontSize: '10px', fontWeight: '800', color: '#64748B', textTransform: 'uppercase', marginBottom: '4px', display: 'block', letterSpacing: '0.6px' }}>
                            Amount (₹)
                        </label>
                        <input 
                            id="edit-amount" 
                            type="number" 
                            style={{ width: '100%', border: 'none', outline: 'none', background: 'transparent', fontSize: '14px', fontWeight: '800', color: '#0F172A', padding: '2px 0' }} 
                            value={editData.amount} 
                            onFocus={() => handleFocusVoice(" यहाँ Salary की नई रकम भरें।")} 
                            onChange={e => setEditData({ ...editData, amount: e.target.value })} 
                        />
                    </div>

                    {/* ⚙️ Row Group 3: Recovery & Period Meta Grid Block */}
                    {editData.type === 'salary' && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                            <div style={{ background: '#F8FAFC', padding: '10px 12px', borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                                <label style={{ fontSize: '10px', fontWeight: '800', color: '#64748B', textTransform: 'uppercase', marginBottom: '4px', display: 'block', letterSpacing: '0.6px' }}>
                                    Recovery (₹)
                                </label>
                                <input 
                                    id="edit-recovery" 
                                    type="number" 
                                    style={{ width: '100%', border: 'none', outline: 'none', background: 'transparent', fontSize: '12px', color: '#EF4444', fontWeight: '700', padding: '2px 0' }} 
                                    value={editData.advanceDed} 
                                    onFocus={() => handleFocusVoice(" यहाँ Advance Recovery रकम भरें।")} 
                                    onChange={e => setEditData({ ...editData, advanceDed: e.target.value })} 
                                />
                            </div>
                            <div style={{ background: '#F8FAFC', padding: '10px 12px', borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                                <label style={{ fontSize: '10px', fontWeight: '800', color: '#64748B', textTransform: 'uppercase', marginBottom: '4px', display: 'block', letterSpacing: '0.6px' }}>
                                    Cycle Period
                                </label>
                                <select 
                                    id="edit-period" 
                                    style={{ width: '100%', border: 'none', outline: 'none', background: 'transparent', fontSize: '12px', fontWeight: '700', color: '#0F172A', cursor: 'pointer', padding: '2px 0' }} 
                                    value={editData.salaryMode} 
                                    onFocus={() => handleFocusVoice(" यहाँ Salary का पीरियड चुनें।")} 
                                    onChange={e => setEditData({ ...editData, salaryMode: e.target.value })}
                                >
                                    <option value="weekly">Weekly</option>
                                    <option value="monthly">Monthly</option>
                                </select>
                            </div>
                        </div>
                    )}

                    {/* ⚙️ Row Group 4: Disbursal Date & Channel Fields Mapping */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <div style={{ background: '#F8FAFC', padding: '10px 12px', borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                            <label style={{ fontSize: '10px', fontWeight: '800', color: '#64748B', textTransform: 'uppercase', marginBottom: '4px', display: 'block', letterSpacing: '0.6px' }}>
                                Disbursal Date
                            </label>
                            <input 
                                id="edit-date" 
                                type="date" 
                                style={{ width: '100%', border: 'none', outline: 'none', background: 'transparent', fontSize: '11px', color: '#334155', fontWeight: '700', padding: '2px 0' }} 
                                value={editData.date} 
                                onFocus={() => handleFocusVoice(" यहाँ नई तारीख चुनें।")} 
                                onChange={e => setEditData({ ...editData, date: e.target.value })} 
                            />
                        </div>
                        <div style={{ background: '#F8FAFC', padding: '10px 12px', borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                            <label style={{ fontSize: '10px', fontWeight: '800', color: '#64748B', textTransform: 'uppercase', marginBottom: '4px', display: 'block', letterSpacing: '0.6px' }}>
                                Payment Channel
                            </label>
                            <select 
                                id="edit-mode" 
                                style={{ width: '100%', border: 'none', outline: 'none', background: 'transparent', fontSize: '12px', fontWeight: '700', color: '#0F172A', cursor: 'pointer', padding: '2px 0' }} 
                                value={editData.baseMode} 
                                onFocus={() => handleFocusVoice(" यहाँ Salary देने का तरीका चुनें।")} 
                                onChange={e => setEditData({ ...editData, baseMode: e.target.value })}
                            >
                                <option value="cash">Liquid Cash</option>
                                <option value="online">Online Transfer</option>
                            </select>
                        </div>
                    </div>

                    {/* ⚙️ Row Group 5: Digital Settlement Widgets Apps Strip */}
                    {editData.baseMode === 'online' && (
                        <div id="edit-online-apps-box" style={{ background: '#FFFFFF', padding: '12px', borderRadius: '16px', border: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', gap: '8px', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.01)' }}>
                            <label style={{ fontSize: '9px', fontWeight: '800', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                                Target Settlement Gateway
                            </label>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                {['PhonePe', 'GPay', 'Paytm', 'BharatPe', 'Other'].map(app => {
                                    let AppIcon = <CreditCard size={12} color={editData.onlineApp === app ? '#FFFFFF' : '#64748B'} />;
                                    if (app === 'PhonePe') AppIcon = <img src="https://www.google.com/s2/favicons?domain=phonepe.com&sz=32" alt="" style={{ width: '12px', height: '12px', borderRadius: '3px' }} />;
                                    else if (app === 'GPay') AppIcon = <img src="https://www.google.com/s2/favicons?domain=pay.google.com&sz=32" alt="" style={{ width: '12px', height: '12px', borderRadius: '3px' }} />;
                                    else if (app === 'Paytm') AppIcon = <img src="https://www.google.com/s2/favicons?domain=paytm.com&sz=32" alt="" style={{ width: '12px', height: '12px', borderRadius: '3px' }} />;
                                    else if (app === 'BharatPe') AppIcon = <img src="https://www.google.com/s2/favicons?domain=bharatpe.com&sz=32" alt="" style={{ width: '12px', height: '12px', borderRadius: '3px' }} />;

                                    const isSelected = editData.onlineApp === app;
                                    return (
                                        <button 
                                            key={app} 
                                            type="button"
                                            onClick={(e) => handleVoiceClick(e, `${app} ऐप का option है।`, () => setEditData({ ...editData, onlineApp: app }), 'app-' + app)}
                                            style={{
                                                padding: '6px 12px', borderRadius: '10px', fontSize: '11px', cursor: 'pointer',
                                                border: isSelected ? '1px solid #0F172A' : '1px solid #E2E8F0',
                                                background: isSelected ? '#0F172A' : '#F8FAFC',
                                                color: isSelected ? '#FFFFFF' : '#475569',
                                                fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '6px',
                                                boxShadow: isSelected ? '0 4px 10px rgba(15, 23, 42, 0.15)' : 'none',
                                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                            }}
                                        >
                                            {AppIcon}
                                            {app}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* ⚙️ Master Execution Update Button Trigger */}
                    <div style={{ marginTop: '6px', width: '100%' }}>
                        <button 
                            id="btn-update-record" 
                            type="button"
                            onClick={(e) => handleVoiceClick(e, " यह अपडेट बटन है।", handleEditSave, 'btn-update-record')}
                            style={{
                                width: '100%', background: '#0F172A', color: '#FFFFFF', border: 'none', 
                                borderRadius: '14px', padding: '13px', fontSize: '13px', fontWeight: '800', 
                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', 
                                gap: '8px', boxShadow: '0 4px 14px rgba(15, 23, 42, 0.2)', textTransform: 'uppercase', 
                                letterSpacing: '0.5px', transition: 'all 0.2s'
                            }}
                            className="micro-click-pill"
                        >
                            Update Payment Record
                        </button>
                    </div>

                </div>
            )}
        </Modal>

           {/* ===== 🌟 PREMIUM DOWNLOAD REPORT MODAL 🌟 ===== */}
            <Modal
                isOpen={showReportModal}
                onClose={closeModalsVoice}
                title="Download Payments Report"
                icon={Download}
                iconColor="#2563EB"
                iconBg="linear-gradient(135deg, #DBEAFE, #93C5FD)"
                maxWidth="400px"
            >
                <div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div>
                             <label style={{ fontSize: '8px', fontWeight: '600', color: '#64748B', textTransform: 'uppercase', marginBottom: '8px', display: 'block', letterSpacing: '0.5px' }}>Select Worker</label>
                             <select id="select-report-worker" style={{ ...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0' }} value={reportConfig.workerId} onFocus={() => handleFocusVoice(" यहाँ वर्कर चुनें जिसकी रिपोर्ट चाहिए।")} onChange={(e) => {
                                 setReportConfig({ ...reportConfig, workerId: e.target.value });
                                 handleSelectVoice(e.target.options[e.target.selectedIndex].text, "रिपोर्ट वर्कर");
                             }}>
                                 <option value="">All Workers</option>{workers.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                             </select>
                         </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <div>
                                <label style={{ fontSize: '8px', fontWeight: '600', color: '#64748B', textTransform: 'uppercase', marginBottom: '8px', display: 'block', letterSpacing: '0.5px' }}>From Date</label>
                                <input 
                                    id="input-report-from"
                                    type="date" 
                                    style={{ ...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0' }} 
                                    value={reportConfig.startDate} 
                                    onFocus={() => handleFocusVoice(" यहाँ रिपोर्ट की शुरुआती तारीख चुनें।")}
                                    onChange={e => setReportConfig({ ...reportConfig, startDate: e.target.value })} 
                                />
                            </div>
                            <div>
                                <label style={{ fontSize: '8px', fontWeight: '600', color: '#64748B', textTransform: 'uppercase', marginBottom: '8px', display: 'block', letterSpacing: '0.5px' }}>To Date</label>
                                <input 
                                    id="input-report-to"
                                    type="date" 
                                    style={{ ...styles.input, background: '#f8fafc', border: '1px solid #e2e8f0' }} 
                                    value={reportConfig.endDate} 
                                    onFocus={() => handleFocusVoice(" यहाँ रिपोर्ट की आखिरी तारीख चुनें।")}
                                    onChange={e => setReportConfig({ ...reportConfig, endDate: e.target.value })} 
                                />
                            </div>
                        </div>
                    </div>

                    <button id="btn-save-pdf" onClick={(e) => handleVoiceClick(e, " यह पीडीएफ जनरेट बटन है।", handleDownloadPdf, 'btn-save-pdf')} 
                        style={{
                            width: '100%', background: 'linear-gradient(135deg, #3B82F6, #2563EB)', color: 'white', border: 'none', borderRadius: '16px', padding: '14px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '24px', transition: 'all 0.2s'
                        }}>
                        <Download size={18} color="white"/> Download Payments Report
                    </button>
                </div>
            </Modal>

           

        </div>
    );
};

const styles = {
    container: { 
        zoom: 0.95,
        fontSize: '14px',
        minHeight: '100vh', 
        background: 'transparent',           
        paddingBottom: '85px',              
        position: 'fixed', 
        top: 0, 
        left: 0, 
        width: '100%', 
        height: '100%', 
        overflowY: 'auto', 
        zIndex: 100,                        
        WebkitOverflowScrolling: 'touch', 
       transform: 'translateZ(0)', 
        backfaceVisibility: 'hidden', 
        perspective: 1000,
        willChange: 'transform, scroll-position'
    },
    header: { background: 'rgba(255, 255, 255, 0.25)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', padding: '12px 16px', borderBottom: '1px solid rgba(255, 255, 255, 0.2)', display: 'flex', alignItems: 'center', justifyContent:'space-between', position:'sticky', top:0, zIndex: 10, willChange: 'transform, backdrop-filter' },
    backBtn: { background: 'rgba(255,255,255,0.4)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.3)', cursor: 'pointer', padding: '4px', display:'flex', alignItems:'center' },
    iconBtnSquare: { width:'32px', height:'32px', borderRadius:'10px', border:'1px solid rgba(255,255,255,0.4)', background:'rgba(255,255,255,0.3)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', transition: 'all 0.2s' },
    content: { padding: '16px', maxWidth: '600px', margin: '0 auto' },
   
    
    tabs: { display: 'flex', background: 'rgba(255,255,255,0.3)', padding: '4px', borderRadius: '16px', marginBottom: '16px', border: '1px solid rgba(255,255,255,0.4)' },
    tabInactive: { flex: 1, padding: '10px', background: 'transparent', border: 'none', borderRadius: '12px', fontSize: '11px', fontWeight: '600', color: '#444', cursor: 'pointer', textTransform:'uppercase', letterSpacing:'0.5px' },
    tabActive: { flex: 1, padding: '10px', background: 'rgba(255,255,255,0.8)', border: 'none', borderRadius: '12px', fontSize: '11px', fontWeight: '600', color: '#111', cursor: 'pointer', transition: 'all 0.2s', textTransform:'uppercase', letterSpacing:'0.5px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' },
    
    /* 🌟 FIX: fontSize को 8px से 11px किया गया ताकि लेबल्स साफ़ और बड़े दिखें */
    fieldLabel: { fontSize: '11px', fontWeight: '600', color: '#64748B', textTransform: 'uppercase', marginBottom: '6px', display: 'block', letterSpacing: '0.5px' },
    input: { width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '11px', outline: 'none', background: '#ffffff', color:'#0F172A', fontWeight:'600', boxSizing: 'border-box', transition: 'all 0.2s', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)' },
    row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' },
    
    choiceBtn: { flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: 'transparent', color: '#64748B', fontWeight: '600', cursor: 'pointer', fontSize:'11px', textTransform:'uppercase', letterSpacing: '0.5px' },
    choiceBtnActive: { flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: '#ffffff', color: '#0F172A', fontWeight: '700', cursor: 'pointer', fontSize:'11px', textTransform:'uppercase', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', letterSpacing: '0.5px' },
     
    modalOverlay: { 
        position: 'fixed', 
        top: '50%',                          /* 🌟 NAYA UPDATE (0 की जगह 50%) */
        left: '50%',                         /* 🌟 NAYA UPDATE (0 की जगह 50%) */
        transform: 'translate(-50%, -50%)',  /* 🌟 NAYA UPDATE (Center lock) */
        width: '120%',                      /* 🌟 NAYA UPDATE (100% की जगह 120vw) */
        height: '120%',                     /* 🌟 NAYA UPDATE (100% की जगह 120vh) */
        background: 'rgba(0, 0, 0, 0.4)',    /* आपका पुराना कोड */
        backdropFilter: 'blur(8px)',         /* आपका पुराना कोड */
        display: 'flex',                     /* 🌟 NAYA UPDATE (grid की जगह flex) */
        alignItems: 'center',                /* 🌟 NAYA UPDATE */
        justifyContent: 'center',            /* 🌟 NAYA UPDATE */
        zIndex: 999999,                      /* 🌟 NAYA UPDATE (9999 से 999999 किया) */
        padding: '16px',                     /* आपका पुराना कोड */
        boxSizing: 'border-box'              /* आपका पुराना कोड */
    },
    modalContent: { background: 'rgba(255, 255, 255, 0.35)', border: '1px solid rgba(255,255,255,0.4)', width: '90%', maxWidth: '320px', borderRadius: '20px', overflow: 'hidden', animation: 'zoomIn 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275)', boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.3)', margin:'auto', zIndex:10001 },
    modalHeader: { padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background:'rgba(255,255,255,0.2)' },
    modalTitle: { fontSize: '11px', fontWeight: '600', color: '#111', margin: 0, letterSpacing:'-0.5px' },
    closeBtn: { background:'rgba(255,255,255,0.4)', border:'1px solid rgba(255,255,255,0.3)', borderRadius:'8px', width:'26px', height:'26px', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' },
    menuDropdown: { 
        position: 'absolute', 
        top: '100%', 
        right: 0, 
        zIndex: 50,
        background: 'rgba(255, 255, 255, 0.95)', 
        backdropFilter: 'blur(16px)', 
        borderRadius: '16px', 
        padding: '6px', 
        width: '180px', 
        boxShadow: '0 8px 32px rgba(0,0,0,0.15)', 
        border: '1px solid rgba(255,255,255,0.6)'
    },

   menuItem: { 
        display:'flex', 
        fontSize: '11px', 
        alignItems:'center', 
        gap:'8px', 
        width:'100%', 
        padding:'8px 10px', 
        marginBottom:'4px', 
        border:'1px solid rgba(255,255,255,0.4)', 
        borderRadius:'10px', 
        background:'rgba(255,255,255,0.5)', 
        cursor:'pointer', 
        textAlign:'left', 
        transition: 'all 0.2s ease' 
    },
    menuText: { 
        fontSize: '14px !important',
        fontWeight: '600', 
        color: '#111' 
    },
    detailRow: { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 0', borderBottom:'1px solid rgba(255,255,255,0.3)' },
    detailLabel: { fontSize:'11px', color:'#444', display:'flex', alignItems:'center', gap:'6px', fontWeight:'600' },
    detailValue: { fontSize:'11px', fontWeight:'600', color:'#111' },
    
    secondaryBtn: { flex:1, padding:'12px', background:'rgba(255,255,255,0.5)', color:'#111', border:'1px solid rgba(255,255,255,0.4)', borderRadius:'12px', fontSize:'11px', fontWeight:'600', cursor:'pointer' },
    
    listItemStyle: { display: 'flex', justifyContent: 'space-between', padding: '12px', borderBottom: '1px solid rgba(255,255,255,0.2)', alignItems: 'center' }
};

export default PaymentView;